import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useOrganization } from '@/hooks/useOrganization';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import salesAgentService, { LeadCaptureState, SalesAgent, SalesAgentUpdate, StickyTriggerPlacement, TerminologyBank, TerminologyEntry, VoiceSettings } from '@/lib/api/salesAgent';
import cartesiaService, { CartesiaVoice } from '@/lib/api/cartesia';
import organizationsService, { PronunciationLibraryEntry } from '@/lib/api/organizations';
import promptVersionsService from '@/lib/api/promptVersions';
import type { PromptVersion } from '@/lib/api/promptVersions';
import customDomainsService from '@/lib/api/customDomains';
import PromptDiffView from '@/components/prompt-diff/PromptDiffView';
import apiClient from '@/lib/api/client';
import type { FileUploadResponse } from '@/lib/api/storage';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  ColorPicker,
  ColorPickerEyeDropper,
  ColorPickerHexInput,
  ColorPickerHue,
  ColorPickerSelection,
} from '@/components/ui/color-picker';
import { ArrowUp, Loader2, Save, User, Sparkles, Video, FileText, Edit, Calendar, Image, Upload, X, ChevronLeft, ChevronRight, AudioLines, Play, Pause, Search, Globe2, MousePointerClick, MicOff, Plus, Trash2, Languages } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { useIsMobile } from '@/hooks/use-mobile';
import { getEmbeddedDemoUrl, getLiveDemoUrl, getLauncherDemoUrl } from '@/lib/demoUrls';
import { cn } from '@/lib/utils';
import { googleFonts } from '@/constants/googleFonts';

const LEAD_CAPTURE_FIELD_OPTIONS: { value: LeadCaptureState; label: string }[] = [
  { value: 'hidden', label: 'Disabled' },
  { value: 'optional', label: 'Optional' },
  { value: 'required', label: 'Required' },
];

const DEFAULT_STICKY_TRIGGER_PLACEMENT: StickyTriggerPlacement = 'bottom-center';

// Tailwind utility fragments for positioning the pill inside preview frames.
// The product UI now uses the widget default rather than exposing placement.
const STICKY_TRIGGER_PLACEMENT_POS_CLASSES: Record<StickyTriggerPlacement, string> = {
  'top-left': 'top-3 left-3',
  'top-center': 'top-3 left-1/2 -translate-x-1/2',
  'top-right': 'top-3 right-3',
  'bottom-left': 'bottom-3 left-3',
  'bottom-center': 'bottom-3 left-1/2 -translate-x-1/2',
  'bottom-right': 'bottom-3 right-3',
};

const LAUNCHER_PREVIEW_ICON_URL = 'https://storage.googleapis.com/hobbes-assets/Favicon.svg';
const LAUNCHER_EMAIL_PLACEHOLDER = 'Enter your work email for a tailored demo';
const DEFAULT_LAUNCHER_TINT = '#14120E';
const DEFAULT_LAUNCHER_ICON_BG = 'rgba(20,18,14,0.88)';

type LauncherPreviewMode = 'compact' | 'rest' | 'composed';
type LauncherPreviewSurface = 'dark' | 'light';
type LauncherPreviewTokens = Record<string, string>;

function launcherDarkTokens(): LauncherPreviewTokens {
  return {
    '--pill-bg': 'rgba(255,255,255,0.10)',
    '--pill-border': 'rgba(255,255,255,0.12)',
    '--pill-fg': 'rgba(255,255,255,0.92)',
    '--pill-fg-soft': 'rgba(255,255,255,0.68)',
    '--pill-fg-faint': 'rgba(255,255,255,0.32)',
    '--accent-ink': '#FBFAF6',
    '--accent-ink-fg': '#14120E',
    '--chip-bg': 'rgba(255,255,255,0.14)',
    '--chip-bg-strong': 'rgba(255,255,255,0.30)',
    '--chip-border': 'rgba(255,255,255,0.12)',
    '--mic-rest-bg': 'rgba(255,255,255,0.18)',
    '--mic-rest-border': 'rgba(255,255,255,0.12)',
    '--mic-active-bg': 'rgba(255,255,255,0.18)',
    '--mic-active-border': 'rgba(255,255,255,0.14)',
    '--field-bg': 'transparent',
    '--launcher-icon-bg': 'rgba(255,255,255,0.12)',
    '--launcher-icon-ring': 'rgba(255,255,255,0.16)',
    '--pill-shadow': '0 16px 40px rgba(0,0,0,0.35)',
  };
}

function launcherLightTokens(): LauncherPreviewTokens {
  return {
    '--pill-bg': 'rgba(251,250,246,0.78)',
    '--pill-border': 'rgba(20,18,14,0.08)',
    '--pill-fg': '#14120E',
    '--pill-fg-soft': 'rgba(20,18,14,0.55)',
    '--pill-fg-faint': 'rgba(20,18,14,0.32)',
    '--accent-ink': '#14120E',
    '--accent-ink-fg': '#FBFAF6',
    '--chip-bg': 'rgba(20,18,14,0.07)',
    '--chip-bg-strong': 'rgba(20,18,14,0.12)',
    '--chip-border': 'rgba(20,18,14,0.10)',
    '--mic-rest-bg': 'rgba(20,18,14,0.07)',
    '--mic-rest-border': 'rgba(20,18,14,0.10)',
    '--mic-active-bg': 'rgba(20,18,14,0.10)',
    '--mic-active-border': 'rgba(20,18,14,0.13)',
    '--field-bg': 'transparent',
    '--launcher-icon-bg': 'rgba(20,18,14,0.88)',
    '--launcher-icon-ring': 'rgba(20,18,14,0.14)',
    '--pill-shadow': '0 1px 2px rgba(0,0,0,0.06), 0 6px 16px rgba(0,0,0,0.10), 0 28px 60px rgba(0,0,0,0.36)',
  };
}

function normalizeHexColor(value: string | null | undefined): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  const match = trimmed.match(/^#?([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (!match) return null;
  const hex = match[1];
  if (hex.length === 3) {
    return `#${hex.split('').map((char) => `${char}${char}`).join('')}`.toUpperCase();
  }
  return `#${hex}`.toUpperCase();
}

function isLightHexColor(value: string | null | undefined): boolean | null {
  const hex = normalizeHexColor(value);
  if (!hex) return null;

  const raw = hex.slice(1);
  const r = Number.parseInt(raw.slice(0, 2), 16);
  const g = Number.parseInt(raw.slice(2, 4), 16);
  const b = Number.parseInt(raw.slice(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  return luminance > 0.5;
}

function launcherFontStack(fontFamily: string | null): string {
  return fontFamily ? `'${fontFamily}', 'Inter', system-ui, sans-serif` : "'Inter', system-ui, sans-serif";
}

function LauncherTintPicker({
  value,
  onChange,
}: {
  value: string | null | undefined;
  onChange: (value: string) => void;
}) {
  const normalizedTint = normalizeHexColor(value);
  const pickerValue = normalizedTint || DEFAULT_LAUNCHER_TINT;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" className="h-11 w-full justify-start gap-2 rounded-2xl px-3">
          <span
            aria-hidden
            className="h-4 w-4 shrink-0 rounded-full border border-border"
            style={{ backgroundColor: pickerValue }}
          />
          <span className="truncate text-sm font-normal">
            {normalizedTint || 'Default'}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 rounded-xl p-3">
        <ColorPicker
          value={pickerValue}
          defaultValue={DEFAULT_LAUNCHER_TINT}
          onChange={(hex) => onChange(hex)}
          className="bg-transparent p-0"
        >
          <ColorPickerSelection className="h-36" />
          <ColorPickerHue />
          <div className="flex items-center gap-2">
            <ColorPickerHexInput />
            <ColorPickerEyeDropper />
          </div>
        </ColorPicker>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="mt-2 h-8 w-full rounded-lg text-muted-foreground"
          onClick={() => onChange('')}
        >
          Clear tint
        </Button>
      </PopoverContent>
    </Popover>
  );
}

function LauncherPreviewBrandIcon({ iconUrl }: { iconUrl: string }) {
  return (
    <span
      aria-hidden
      className="flex h-6 w-6 shrink-0 items-center justify-center overflow-hidden rounded-full"
      style={{
        backgroundColor: 'var(--launcher-icon-bg)',
        boxShadow: '0 0 0 0.5px var(--launcher-icon-ring) inset, 0 1px 3px rgba(0,0,0,0.18)',
      }}
    >
      <img src={iconUrl} alt="" draggable={false} className="h-[76%] w-[76%] object-contain" />
    </span>
  );
}

// Mirrors the latest hobbesWidget launcher: compact icon, rest CTA pill, and
// composed inline capture surface. Kept local so Platform can preview widget
// styling without coupling directly to the widget runtime.
function StickyTriggerMorphPreview({
  placement,
  iconUrl,
  text,
  tintColor,
  textColor,
  fontFamily,
  thumbnailUrl,
  leadCaptureEmail,
  iconBgColor,
}: {
  placement: StickyTriggerPlacement;
  iconUrl: string;
  text: string;
  tintColor: string | null;
  textColor: string | null;
  fontFamily: string | null;
  thumbnailUrl: string;
  leadCaptureEmail: LeadCaptureState;
  iconBgColor?: string | null;
}) {
  const [previewMode, setPreviewMode] = React.useState<LauncherPreviewMode>('composed');
  const [previewSurface, setPreviewSurface] = React.useState<LauncherPreviewSurface>('dark');
  const measureRef = React.useRef<HTMLSpanElement>(null);
  const resolvedIconUrl = iconUrl || LAUNCHER_PREVIEW_ICON_URL;
  const label = text || 'Start Demo';
  const normalizedTint = normalizeHexColor(tintColor);
  const normalizedTextColor = normalizeHexColor(textColor);
  const normalizedIconBg = normalizeHexColor(iconBgColor);
  const inferredLightTheme = isLightHexColor(normalizedTint) === true;
  const tokens = {
    ...(inferredLightTheme ? launcherLightTokens() : launcherDarkTokens()),
    ...(normalizedTint ? { '--pill-bg': normalizedTint } : {}),
    ...(normalizedTextColor ? { '--pill-fg': normalizedTextColor } : {}),
    ...(normalizedIconBg ? { '--launcher-icon-bg': normalizedIconBg } : {}),
  };
  const isDarkPreviewSurface = previewSurface === 'dark';
  const isTopPlacement = placement.startsWith('top');
  const showStarterChips = leadCaptureEmail !== 'required' && previewMode === 'composed';
  const placeholder = leadCaptureEmail === 'hidden' ? 'Type your message here...' : LAUNCHER_EMAIL_PLACEHOLDER;
  const shouldShowThumbnail = Boolean(thumbnailUrl) && previewMode === 'composed';
  const fallbackRestWidth = Math.max(96, Math.ceil(label.length * 7.2) + (resolvedIconUrl ? 42 : 18));
  const [measuredRestWidth, setMeasuredRestWidth] = React.useState<number | null>(null);
  const composedWidth = Math.max(438, Math.ceil(placeholder.length * 7.4) + 122);
  const restWidth = measuredRestWidth ?? fallbackRestWidth;
  const launcherWidth = previewMode === 'compact' ? 40 : previewMode === 'rest' ? restWidth : composedWidth;

  React.useLayoutEffect(() => {
    const measure = () => {
      if (!measureRef.current) return;
      const width = Math.ceil(measureRef.current.getBoundingClientRect().width);
      if (width > 0) setMeasuredRestWidth(width);
    };

    measure();
    document.fonts?.ready.then(measure).catch(() => undefined);
  }, [fallbackRestWidth, fontFamily, label, resolvedIconUrl]);

  return (
    <div className={cn('absolute inset-0 overflow-hidden', isDarkPreviewSurface ? 'bg-[#0b0f14]' : 'bg-[#f7f6f2]')}>
      <span
        ref={measureRef}
        aria-hidden
        className="absolute pointer-events-none invisible inline-flex items-center gap-2 whitespace-nowrap"
        style={{
          top: -9999,
          left: -9999,
          height: 40,
          paddingLeft: 10,
          paddingRight: 8,
          gap: resolvedIconUrl ? 8 : 0,
          borderWidth: 1,
          borderStyle: 'solid',
          borderColor: 'transparent',
          boxSizing: 'border-box',
          fontFamily: launcherFontStack(fontFamily),
        }}
      >
        <span className="h-6 w-6 shrink-0 rounded-full" />
        <span className="text-[13px] font-medium leading-none">{label}</span>
      </span>

      <div
        className={cn(
          'absolute inset-0',
          isDarkPreviewSurface
            ? 'bg-[radial-gradient(circle_at_18%_18%,rgba(99,102,241,0.18),transparent_30%),radial-gradient(circle_at_82%_78%,rgba(20,184,166,0.14),transparent_28%),linear-gradient(180deg,#151923_0%,#0b0f14_100%)]'
            : 'bg-[radial-gradient(circle_at_18%_18%,rgba(20,18,14,0.08),transparent_28%),radial-gradient(circle_at_82%_78%,rgba(20,184,166,0.10),transparent_30%),linear-gradient(180deg,#fbfaf6_0%,#ece9e2_100%)]',
        )}
      />
      <div className="absolute left-6 right-6 top-6 space-y-3">
        <div className={cn('h-2 w-24 rounded-full', isDarkPreviewSurface ? 'bg-white/20' : 'bg-black/[0.12]')} />
        <div className="grid grid-cols-3 gap-3">
          <div className={cn('h-10 rounded-md border', isDarkPreviewSurface ? 'border-white/10 bg-white/[0.08]' : 'border-black/[0.08] bg-white/[0.72]')} />
          <div className={cn('h-10 rounded-md border', isDarkPreviewSurface ? 'border-white/10 bg-white/[0.06]' : 'border-black/[0.08] bg-white/[0.58]')} />
          <div className={cn('h-10 rounded-md border', isDarkPreviewSurface ? 'border-white/10 bg-white/[0.04]' : 'border-black/[0.08] bg-white/[0.44]')} />
        </div>
        <div className={cn('h-24 rounded-lg border', isDarkPreviewSurface ? 'border-white/10 bg-white/[0.06]' : 'border-black/[0.08] bg-white/[0.54]')} />
      </div>

      <div
        className={cn(
          'absolute left-3 top-3 z-10 flex rounded-md border p-0.5 text-[10px] font-medium shadow-sm backdrop-blur',
          isDarkPreviewSurface ? 'border-white/10 bg-black/25 text-white/60' : 'border-black/10 bg-white/70 text-black/55',
        )}
        aria-label="Preview background"
      >
        {[
          { value: 'dark', label: 'Dark' },
          { value: 'light', label: 'Light' },
        ].map((surface) => (
          <button
            key={surface.value}
            type="button"
            onClick={() => setPreviewSurface(surface.value as LauncherPreviewSurface)}
            className={cn(
              'rounded px-2 py-1 transition-colors',
              previewSurface === surface.value
                ? isDarkPreviewSurface ? 'bg-white text-black shadow-sm' : 'bg-black text-white shadow-sm'
                : isDarkPreviewSurface ? 'hover:text-white' : 'hover:text-black',
            )}
          >
            {surface.label}
          </button>
        ))}
      </div>

      <div className={cn('absolute right-3 top-3 z-10 flex rounded-md border p-0.5 text-[10px] font-medium shadow-sm backdrop-blur', isDarkPreviewSurface ? 'border-white/10 bg-black/25 text-white/60' : 'border-black/10 bg-white/70 text-black/55')}>
        {[
          { value: 'compact', label: 'Icon' },
          { value: 'rest', label: 'Rest' },
          { value: 'composed', label: 'Open' },
        ].map((mode) => (
          <button
            key={mode.value}
            type="button"
            onClick={() => setPreviewMode(mode.value as LauncherPreviewMode)}
              className={cn(
                'rounded px-2 py-1 transition-colors',
                previewMode === mode.value
                  ? isDarkPreviewSurface ? 'bg-white text-black shadow-sm' : 'bg-black text-white shadow-sm'
                  : isDarkPreviewSurface ? 'hover:text-white' : 'hover:text-black',
              )}
          >
            {mode.label}
          </button>
        ))}
      </div>

      <div
        className={cn(
          'absolute flex flex-col items-center',
          STICKY_TRIGGER_PLACEMENT_POS_CLASSES[placement],
        )}
        style={{ ...tokens, fontFamily: launcherFontStack(fontFamily) } as React.CSSProperties}
      >
        {showStarterChips ? (
          <div className={cn('absolute left-0 flex flex-col items-start gap-2', isTopPlacement ? 'top-full mt-3' : 'bottom-full mb-3')}>
            {['See Pricing', 'Talk to Sales'].map((chip) => (
              <span
                key={chip}
                className="rounded-full border px-[10px] py-2 text-left text-[12px] font-medium leading-none"
                style={{
                  backgroundColor: 'var(--pill-bg)',
                  borderColor: 'var(--pill-border)',
                  color: 'var(--pill-fg)',
                  boxShadow: 'none',
                  backdropFilter: 'blur(6px) saturate(160%)',
                  WebkitBackdropFilter: 'blur(6px) saturate(160%)',
                }}
              >
                {chip}
              </span>
            ))}
          </div>
        ) : null}

        {shouldShowThumbnail ? (
          <div
            className={cn(
              'absolute left-1/2 max-w-[calc(100vw-96px)] -translate-x-1/2 overflow-hidden border',
              isTopPlacement ? 'top-full mt-2' : 'bottom-full mb-2',
            )}
            style={{
              width: launcherWidth,
              aspectRatio: '16 / 9',
              borderRadius: 14,
              backgroundColor: 'var(--pill-bg)',
              borderColor: 'var(--pill-border)',
              backdropFilter: 'blur(6px) saturate(160%)',
              WebkitBackdropFilter: 'blur(6px) saturate(160%)',
            } as React.CSSProperties}
          >
            <img src={thumbnailUrl} alt="" className="h-full w-full object-cover" draggable={false} />
          </div>
        ) : null}

        <div
          className="relative overflow-hidden border select-none transition-all duration-300"
          style={{
            height: 40,
            width: launcherWidth,
            borderRadius: 999,
            backgroundColor: 'var(--pill-bg)',
            borderColor: 'var(--pill-border)',
            color: 'var(--pill-fg)',
            boxShadow: 'var(--pill-shadow)',
            backdropFilter: 'blur(6px) saturate(160%)',
            WebkitBackdropFilter: 'blur(6px) saturate(160%)',
          } as React.CSSProperties}
        >
          {previewMode === 'compact' ? (
            <div className="absolute inset-0 flex items-center justify-center">
              <LauncherPreviewBrandIcon iconUrl={resolvedIconUrl} />
            </div>
          ) : null}

          {previewMode === 'rest' ? (
            <div className="absolute inset-0 flex items-center gap-2 px-2">
              <LauncherPreviewBrandIcon iconUrl={resolvedIconUrl} />
              <span className="shrink-0 whitespace-nowrap text-left text-[13px] font-medium leading-none">
                {label}
              </span>
            </div>
          ) : null}

          {previewMode === 'composed' ? (
            <div className="absolute inset-0 flex items-center gap-2 px-1.5">
              <LauncherPreviewBrandIcon iconUrl={resolvedIconUrl} />
              <span
                className="min-w-0 flex-1 whitespace-nowrap text-left leading-none"
                style={{
                  color: 'var(--pill-fg-faint)',
                  fontFamily: 'inherit',
                  fontSize: 14,
                  fontWeight: 400,
                  letterSpacing: 0,
                }}
              >
                {placeholder}
              </span>
              <span
                className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border"
                style={{
                  backgroundColor: 'var(--mic-rest-bg)',
                  borderColor: 'var(--mic-rest-border)',
                  color: 'var(--pill-fg-soft)',
                  transition: 'background-color 200ms ease, border-color 200ms ease, color 200ms ease',
                }}
              >
                <MicOff className="h-3.5 w-3.5" />
              </span>
              <span
                className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full"
                style={{
                  backgroundColor: 'var(--accent-ink)',
                  color: 'var(--accent-ink-fg)',
                  boxShadow: '0 1px 0 rgba(255,255,255,0.10) inset, 0 -1px 0 rgba(0,0,0,0.30) inset, 0 4px 10px rgba(0,0,0,0.25)',
                }}
              >
                <ArrowUp className="h-3.5 w-3.5" />
              </span>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

const CONVERSATION_CONTROL_MODE_OPTIONS = [
  {
    value: 'exploratory',
    label: 'Exploratory',
    description: 'Lets the visitor guide more of the path with lighter direction.',
  },
  {
    value: 'balanced',
    label: 'Balanced',
    description: 'Leads when the next step is obvious, but still pauses at real branch points.',
  },
  {
    value: 'directive',
    label: 'Directive',
    description: 'Drives the strongest next proof point proactively with minimal permission-seeking.',
  },
] as const;

const getConversationControlModeOption = (value?: string | null) =>
  CONVERSATION_CONTROL_MODE_OPTIONS.find((option) => option.value === value)
  || CONVERSATION_CONTROL_MODE_OPTIONS.find((option) => option.value === 'balanced')!;

const getLeadCaptureStateLabel = (value?: LeadCaptureState | null) => {
  const option = LEAD_CAPTURE_FIELD_OPTIONS.find((item) => item.value === value);
  return option?.label || 'Disabled';
};

type ConfigSection = 'general' | 'voice' | 'conversation' | 'content' | 'calendar' | 'entryModal' | 'launcherTrigger' | 'launchState' | 'terminology';

const CONFIG_SECTIONS: {
  key: ConfigSection;
  icon: React.FC<{ className?: string }>;
  label: string;
  description: string;
}[] = [
  { key: 'general', icon: User, label: 'General', description: 'Status and operational contact details.' },
  { key: 'voice', icon: AudioLines, label: 'Voice', description: 'Voice selection, style, preview, and cadence.' },
  { key: 'conversation', icon: Sparkles, label: 'Conversation', description: 'Control how strongly the agent leads and how suggestions behave.' },
  { key: 'content', icon: FileText, label: 'Content Sources', description: 'Control which assets the agent can reference.' },
  { key: 'calendar', icon: Calendar, label: 'Calendar', description: 'Booking destination used for follow-up meetings.' },
  { key: 'entryModal', icon: Image, label: 'Entry Modal', description: 'The visitor-facing entry experience before the demo starts.' },
  { key: 'launcherTrigger', icon: MousePointerClick, label: 'Launcher Trigger', description: 'Customize the launcher widget launch button.' },
  { key: 'launchState', icon: Image, label: 'Launch State', description: 'What the viewer shows before demo content begins.' },
  { key: 'terminology', icon: Languages, label: 'Terminology', description: 'Regional terminology banks for localized agent language.' },
];

const createEmptyFormData = (): Partial<SalesAgent> => ({
  name: '',
  is_active: true,
  conversation_control_mode: 'balanced',
  use_active_deck: true,
  use_all_videos: true,
  use_documentation: false,
  contact_email: '',
  calendar_link: '',
  suggestion_instructions: '',
  auto_suggestions_enabled: true,
  entry_modal_logo_url: '',
  header_logo_url: '',
  entry_modal_title: '',
  entry_modal_description: '',
  entry_modal_button_text: '',
  lead_capture_name: 'hidden',
  lead_capture_email: 'required',
  lead_capture_phone: 'hidden',
  lead_capture_business: 'hidden',
  background_screenshot_url: '',
  sticky_trigger_icon_url: '',
  sticky_trigger_text: '',
  sticky_trigger_bg_color: '',
  sticky_trigger_text_color: '',
  sticky_trigger_font_family: null,
  sticky_trigger_placement: null,
  sticky_trigger_thumbnail_url: '',
  launcher_icon_bg_color: '',
  voice_settings: null,
  terminology_bank: null,
});

const mapSalesAgentToFormData = (agent: SalesAgent | null | undefined): Partial<SalesAgent> => {
  if (!agent) {
    return createEmptyFormData();
  }

  return {
    ...createEmptyFormData(),
    ...agent,
    voice_settings: agent.voice_settings
      ? {
          ...agent.voice_settings,
        }
      : null,
  };
};

const VOICE_EMOTIONS = [
  { value: 'neutral', label: 'Neutral', description: 'Balanced and steady delivery.' },
  { value: 'content', label: 'Content', description: 'Warm, comfortable, and settled.' },
  { value: 'happy', label: 'Happy', description: 'Positive and upbeat without overdoing it.' },
  { value: 'confident', label: 'Confident', description: 'Assured, polished, and direct.' },
  { value: 'calm', label: 'Calm', description: 'Relaxed and measured pacing.' },
  { value: 'excited', label: 'Excited', description: 'High-energy and animated.' },
  { value: 'enthusiastic', label: 'Enthusiastic', description: 'Engaged and encouraging.' },
  { value: 'curious', label: 'Curious', description: 'Inquisitive and exploratory.' },
  { value: 'determined', label: 'Determined', description: 'Focused and purposeful.' },
  { value: 'surprised', label: 'Surprised', description: 'Expressive with a sense of discovery.' },
  { value: 'proud', label: 'Proud', description: 'Confident with celebratory emphasis.' },
  { value: 'peaceful', label: 'Peaceful', description: 'Soft, composed, and smooth.' },
  { value: 'skeptical', label: 'Skeptical', description: 'Questioning and slightly guarded.' },
  { value: 'sarcastic', label: 'Sarcastic', description: 'Dry and ironic.' },
  { value: 'frustrated', label: 'Frustrated', description: 'Tense with restrained irritation.' },
  { value: 'melancholic', label: 'Melancholic', description: 'Heavy and reflective.' },
  { value: 'anxious', label: 'Anxious', description: 'Uneasy and urgent.' },
  { value: 'sad', label: 'Sad', description: 'Low-energy and subdued.' },
  { value: 'scared', label: 'Scared', description: 'Nervous and apprehensive.' },
  { value: 'angry', label: 'Angry', description: 'Sharp and intense.' },
  { value: 'elated', label: 'Elated', description: 'Very elevated and celebratory.' },
];

const VALID_VOICE_EMOTIONS = new Set([
  'affectionate',
  'agitated',
  'alarmed',
  'amazed',
  'angry',
  'anxious',
  'anticipation',
  'apologetic',
  'bored',
  'calm',
  'confident',
  'confused',
  'contempt',
  'content',
  'contemplative',
  'curious',
  'dejected',
  'determined',
  'disappointed',
  'disgusted',
  'distant',
  'elated',
  'envious',
  'euphoric',
  'happy',
  'hesitant',
  'frustrated',
  'flirtatious',
  'grateful',
  'guilty',
  'hurt',
  'insecure',
  'ironic',
  'joking/comedic',
  'mad',
  'melancholic',
  'mysterious',
  'neutral',
  'nostalgic',
  'outraged',
  'panicked',
  'peaceful',
  'proud',
  'rejected',
  'resigned',
  'sad',
  'sarcastic',
  'scared',
  'skeptical',
  'sympathetic',
  'surprised',
  'serene',
  'threatened',
  'tired',
  'triumphant',
  'trust',
  'wistful',
  'enthusiastic',
  'excited',
]);
const VOICES_PER_PAGE = 8;
const MAX_PRONUNCIATION_ENTRIES = 250;
type VoiceFilter = 'recommended' | 'all' | 'feminine' | 'masculine';
type VoiceEditorPanel = 'voice' | 'style' | 'speed';
const RECOMMENDED_VOICE_KEYWORDS = [
  'brooke',
  'carson',
  'jacqueline',
  'katie',
  'kiefer',
  'ronald',
  'caroline',
  'cathy',
  'blake',
];
const NON_PROFESSIONAL_VOICE_KEYWORDS = [
  'disgust',
  'angry',
  'sarcastic',
  'scared',
  'anxious',
  'melancholic',
  'frustrated',
  'big sister',
  'coworker',
];

const normalizeVoiceName = (name?: string | null) =>
  (name || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const matchesVoiceGender = (voice: CartesiaVoice, target: 'feminine' | 'masculine') => {
  const normalizedGender = normalizeVoiceName(voice.gender);
  const normalizedDescription = normalizeVoiceName(voice.description);
  if (target === 'feminine') {
    return normalizedGender.includes('feminine') || normalizedDescription.includes('female');
  }
  return normalizedGender.includes('masculine') || normalizedDescription.includes('male');
};

const describeVoiceSpeed = (speed: number) => {
  if (speed <= 0.8) return 'Measured';
  if (speed >= 1.15) return 'Energetic';
  return 'Natural';
};

const sanitizeVoiceProviderMessage = (value?: string | null) =>
  value?.replace(/cartesia/gi, 'voice provider').trim() || null;

const normalizePronunciationEntries = (entries: PronunciationLibraryEntry[]) =>
  entries
    .map((entry) => ({
      text: entry.text.trim(),
      alias: entry.alias.trim(),
    }))
    .filter((entry) => entry.text.length > 0 || entry.alias.length > 0);

export const SalesAgentContent: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const rawVersionParam = searchParams.get('version');
  const versionNumber = rawVersionParam ? Number.parseInt(rawVersionParam, 10) : null;
  const rawBaseParam = searchParams.get('base');
  const rawCompareParam = searchParams.get('compare');
  const rawSelectedParam = searchParams.get('selected');
  const baseVersion = rawBaseParam ? Number.parseInt(rawBaseParam, 10) : null;
  const compareVersion = rawCompareParam ? Number.parseInt(rawCompareParam, 10) : null;
  const selectedVersion = rawSelectedParam ? Number.parseInt(rawSelectedParam, 10) : null;
  const isComparingTwo = baseVersion !== null && Number.isInteger(baseVersion) && baseVersion > 0
    && compareVersion !== null && Number.isInteger(compareVersion) && compareVersion > 0;
  const isViewingVersion = isComparingTwo || (versionNumber !== null && Number.isInteger(versionNumber) && versionNumber > 0);

  // Determine which version is the user's selection and which is the comparison
  // target (changeable via dropdown). Works for both two-version and single-version views.
  const resolvedSelectedVersion = isComparingTwo
    ? (selectedVersion === baseVersion ? baseVersion
      : selectedVersion === compareVersion ? compareVersion
      : compareVersion) // fallback: treat the higher version as selected
    : versionNumber;
  const compareTargetVersion = isComparingTwo && resolvedSelectedVersion !== null
    ? (resolvedSelectedVersion === baseVersion ? compareVersion : baseVersion)
    : null;
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.id;
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const [voiceSearch, setVoiceSearch] = useState('');
  const [styleSearch, setStyleSearch] = useState('');
  const [voiceFilter, setVoiceFilter] = useState<VoiceFilter>('recommended');
  const [voiceEditorPanel, setVoiceEditorPanel] = useState<VoiceEditorPanel>('voice');
  const [voicePage, setVoicePage] = useState(1);
  const [previewingVoiceId, setPreviewingVoiceId] = useState<string | null>(null);
  const [activePreviewVoiceId, setActivePreviewVoiceId] = useState<string | null>(null);
  const [previewAudio, setPreviewAudio] = useState<HTMLAudioElement | null>(null);
  const [pronunciationSearch, setPronunciationSearch] = useState('');
  const [pronunciationEntries, setPronunciationEntries] = useState<PronunciationLibraryEntry[]>([]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.innerWidth >= 768) {
      setMobileSectionView('detail');
    } else {
      setMobileSectionView('list');
    }
  }, [isMobile]);

  const [isEditing, setIsEditing] = useState(false);
  const [activeViewSection, setActiveViewSection] = useState<ConfigSection>('general');
  const [activeEditSection, setActiveEditSection] = useState<ConfigSection>('general');
  const [mobileSectionView, setMobileSectionView] = useState<'list' | 'detail'>('list');
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [isUploadingHeaderLogo, setIsUploadingHeaderLogo] = useState(false);
  const [isUploadingBackground, setIsUploadingBackground] = useState(false);
  const [isUploadingLauncherIcon, setIsUploadingLauncherIcon] = useState(false);
  const [isUploadingLauncherThumbnail, setIsUploadingLauncherThumbnail] = useState(false);
  const logoInputRef = React.useRef<HTMLInputElement>(null);
  const headerLogoInputRef = React.useRef<HTMLInputElement>(null);
  const backgroundInputRef = React.useRef<HTMLInputElement>(null);
  const launcherIconInputRef = React.useRef<HTMLInputElement>(null);
  const launcherThumbnailInputRef = React.useRef<HTMLInputElement>(null);

  const selectViewSection = (section: ConfigSection) => {
    setActiveViewSection(section);
    if (isMobile) {
      setMobileSectionView('detail');
    }
  };

  const sectionNavBase =
    'w-full rounded-md px-2.5 py-2.5 text-left transition-colors hover:bg-muted/35';
  const sectionNavActive = 'bg-muted/55 text-foreground';
  const workspaceRailButtonClass = (isActive: boolean) =>
    cn(sectionNavBase, isActive && sectionNavActive);
  const sectionButtonClass = (section: ConfigSection) =>
    workspaceRailButtonClass(activeViewSection === section);
  const [formData, setFormData] = useState<Partial<SalesAgent>>(() => createEmptyFormData());
  const currentSection = isEditing ? activeEditSection : activeViewSection;
  const currentSectionMeta = CONFIG_SECTIONS.find((section) => section.key === currentSection) ?? CONFIG_SECTIONS[0];

  // Fetch existing sales agent
  const { data: salesAgent, isLoading } = useQuery({
    queryKey: ['sales-agent', orgId],
    queryFn: async () => {
      if (!orgId) throw new Error('Organization ID required');
      const response = await salesAgentService.getSalesAgent(orgId);
      if (response.success && response.data) {
        return response.data;
      }
      return null;
    },
    enabled: !!orgId,
    retry: false,
  });

  const conversationModeOption = getConversationControlModeOption(
    salesAgent?.conversation_control_mode ?? formData.conversation_control_mode
  );

  const { data: customDomainState } = useQuery({
    queryKey: ['custom-domain', orgId],
    queryFn: async () => {
      if (!orgId) throw new Error('Organization ID required');
      const response = await customDomainsService.getCustomDomain(orgId);
      if (response.success && response.data) return response.data;
      return null;
    },
    enabled: !!orgId,
    retry: false,
  });

  const liveDemoUrl = getLiveDemoUrl(salesAgent?.demo_slug, customDomainState?.custom_domain);
  const embeddedDemoUrl = getEmbeddedDemoUrl(salesAgent?.demo_slug);
  const launcherDemoUrl = getLauncherDemoUrl(salesAgent?.demo_slug);

  useEffect(() => {
    if (salesAgent) {
      setFormData(mapSalesAgentToFormData(salesAgent));
      setIsEditing(false);
    } else if (!isLoading) {
      setFormData(createEmptyFormData());
      setIsEditing(false);
    }
  }, [isLoading, salesAgent]);

  const { data: voiceCatalog, isLoading: isVoiceCatalogLoading } = useQuery({
    queryKey: ['cartesia-voices'],
    queryFn: async () => {
      const response = await cartesiaService.listVoices();
      if (!response.success || !response.data) {
        throw new Error(
          sanitizeVoiceProviderMessage(response.error) || 'Failed to load available voices'
        );
      }
      return response.data.voices;
    },
    enabled: !isViewingVersion,
    staleTime: 5 * 60 * 1000,
  });


  const { data: terminologyCountries } = useQuery({
    queryKey: ['terminology-countries', orgId],
    queryFn: async () => {
      if (!orgId) throw new Error('Organization ID required');
      const response = await salesAgentService.getTerminologyCountries(orgId);
      if (response.success && response.data) return response.data;
      return [] as string[];
    },
    enabled: !!orgId && !isViewingVersion,
    staleTime: 10 * 60 * 1000,
  });

  const { data: pronunciationLibrary, isLoading: isPronunciationLibraryLoading } = useQuery({
    queryKey: ['pronunciation-library', orgId],
    queryFn: async () => {
      if (!orgId) throw new Error('Organization ID required');
      const response = await organizationsService.getPronunciationLibrary(orgId);
      if (!response.success || !response.data) {
        throw new Error(
          sanitizeVoiceProviderMessage(response.error) || 'Failed to load pronunciation library'
        );
      }
      return response.data;
    },
    enabled: !!orgId && !isViewingVersion,
    staleTime: 60 * 1000,
  });

  // Fetch version diff when viewing a specific version
  const { data: versionDiff, isLoading: isVersionLoading } = useQuery({
    queryKey: ['prompt-version-diff', orgId, isComparingTwo ? `${baseVersion}-${compareVersion}` : versionNumber],
    queryFn: async () => {
      if (!orgId) return null;
      if (isComparingTwo) {
        const res = await promptVersionsService.getVersionDiffBetween(orgId, baseVersion!, compareVersion!);
        if (res.success && res.data) return res.data;
        return null;
      }
      if (versionNumber === null) return null;
      const res = await promptVersionsService.getVersionDiff(orgId, versionNumber);
      if (res.success && res.data) return res.data;
      return null;
    },
    enabled: !!orgId && isViewingVersion,
  });

  // Fetch versions list for the comparison target dropdown
  const { data: versionsList } = useQuery({
    queryKey: ['prompt-versions', orgId],
    queryFn: async () => {
      if (!orgId) return null;
      const res = await promptVersionsService.listVersions(orgId);
      if (res.success && res.data) return res.data;
      return null;
    },
    enabled: !!orgId && isViewingVersion,
  });

  // Versions available in the "compare against" dropdown (exclude the selected version)
  const compareDropdownOptions = (versionsList?.versions ?? []).filter(
    (v) => v.version !== resolvedSelectedVersion,
  );
  const baseVersionMetadata: PromptVersion | null = versionDiff
    ? (versionsList?.versions ?? []).find((v) => v.version === versionDiff.base_version) ?? null
    : null;
  const compareVersionMetadata: PromptVersion | null = versionDiff
    ? (versionsList?.versions ?? []).find((v) => v.version === versionDiff.compare_version) ?? null
    : null;

  const handleCompareTargetChange = (newTargetValue: string) => {
    const newTarget = Number.parseInt(newTargetValue, 10);
    if (!resolvedSelectedVersion || isNaN(newTarget) || newTarget === compareTargetVersion) return;
    const base = Math.min(newTarget, resolvedSelectedVersion);
    const compare = Math.max(newTarget, resolvedSelectedVersion);
    navigate(
      `/agents/configure?base=${base}&compare=${compare}&selected=${resolvedSelectedVersion}`,
      { replace: true },
    );
  };

  useEffect(() => {
    return () => {
      if (previewAudio) {
        previewAudio.pause();
      }
    };
  }, [previewAudio]);

  useEffect(() => {
    setPronunciationEntries(pronunciationLibrary?.entries ?? []);
  }, [pronunciationLibrary?.entries]);

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (data: SalesAgentUpdate) => {
      if (!orgId) throw new Error('Organization ID required');
      const response = await salesAgentService.updateSalesAgent(orgId, data);
      if (!response.success) {
        throw new Error(response.error || 'Failed to update sales agent');
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sales-agent', orgId] });
      setIsEditing(false);
      toast({
        title: 'Success',
        description: 'Sales agent updated successfully',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const savePronunciationLibraryMutation = useMutation({
    mutationFn: async (entries: PronunciationLibraryEntry[]) => {
      if (!orgId) throw new Error('Organization ID required');
      const response = await organizationsService.updatePronunciationLibrary(orgId, { entries });
      if (!response.success || !response.data) {
        throw new Error(
          sanitizeVoiceProviderMessage(response.error) || 'Failed to save pronunciation library'
        );
      }
      return response.data;
    },
    onSuccess: (data) => {
      setPronunciationEntries(data.entries || []);
      queryClient.invalidateQueries({ queryKey: ['pronunciation-library', orgId] });
      toast({
        title: 'Pronunciation library saved',
        description: 'Your pronunciation updates will now apply across this organization.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Unable to save pronunciation library',
        description: sanitizeVoiceProviderMessage(error.message) || 'Unable to save pronunciation library.',
        variant: 'destructive',
      });
    },
  });

  const englishVoices = (voiceCatalog || []).filter((voice) => {
    const language = voice.language?.toLowerCase();
    return language === 'en' || language?.startsWith('en_') || language?.startsWith('en-');
  });

  const professionalEnglishVoices = englishVoices.filter((voice) => {
    const normalizedMetadata = normalizeVoiceName(`${voice.name || ''} ${voice.description || ''}`);
    return !NON_PROFESSIONAL_VOICE_KEYWORDS.some((keyword) => normalizedMetadata.includes(keyword));
  });

  const recommendedVoices = professionalEnglishVoices.filter((voice) => {
    const normalizedName = normalizeVoiceName(voice.name);
    return RECOMMENDED_VOICE_KEYWORDS.some((keyword) => normalizedName.includes(keyword));
  });
  const feminineVoices = professionalEnglishVoices.filter((voice) => matchesVoiceGender(voice, 'feminine'));
  const masculineVoices = professionalEnglishVoices.filter((voice) => matchesVoiceGender(voice, 'masculine'));
  const usingRecommendedFallback = voiceFilter === 'recommended' && recommendedVoices.length === 0;
  const visibleVoices = (() => {
    if (voiceFilter === 'recommended') {
      return usingRecommendedFallback ? professionalEnglishVoices : recommendedVoices;
    }
    if (voiceFilter === 'feminine') return feminineVoices;
    if (voiceFilter === 'masculine') return masculineVoices;
    return professionalEnglishVoices;
  })();

  const filteredVoices = visibleVoices.filter((voice) => {
    const query = voiceSearch.trim().toLowerCase();
    if (!query) return true;
    return [voice.name, voice.description, voice.language, voice.gender]
      .filter(Boolean)
      .some((value) => value!.toLowerCase().includes(query));
  });

  const selectedVoiceId = formData.voice_settings?.voice_id?.trim() || '';
  const selectedVoice = englishVoices.find((voice) => voice.id === selectedVoiceId)
    || (voiceCatalog || []).find((voice) => voice.id === selectedVoiceId)
    || null;
  const savedVoiceId = salesAgent?.voice_settings?.voice_id?.trim() || '';
  const savedVoice = englishVoices.find((voice) => voice.id === savedVoiceId)
    || (voiceCatalog || []).find((voice) => voice.id === savedVoiceId)
    || null;
  const selectedVoiceLabel = selectedVoice?.name || selectedVoiceId || 'No voice selected';
  const savedVoiceLabel = savedVoice?.name || savedVoiceId || 'No voice selected';
  const savedVoiceLanguage = savedVoice?.language?.trim() || null;
  const selectedEmotion = (formData.voice_settings?.emotion ?? '').trim() || 'confident';
  const selectedSpeed = formData.voice_settings?.speed ?? 0.9;
  const voicePanelCardClass = (panel: VoiceEditorPanel) => cn(
    'p-3 rounded-lg border text-left',
    voiceEditorPanel === panel
      ? 'border-primary bg-primary/5'
      : 'card-border bg-gray-50/50 dark:bg-zinc-900/50'
  );
  const totalVoicePages = Math.max(1, Math.ceil(filteredVoices.length / VOICES_PER_PAGE));
  const paginatedVoices = filteredVoices.slice((voicePage - 1) * VOICES_PER_PAGE, voicePage * VOICES_PER_PAGE);
  const filteredStyles = VOICE_EMOTIONS.filter((emotion) => {
    const query = styleSearch.trim().toLowerCase();
    if (!query) return true;
    return [emotion.label, emotion.value, emotion.description]
      .some((value) => value.toLowerCase().includes(query));
  });
  const filteredPronunciationRows = pronunciationEntries
    .map((entry, index) => ({ entry, index }))
    .filter(({ entry }) => {
      const query = pronunciationSearch.trim().toLowerCase();
      if (!query) return true;
      return [entry.text, entry.alias].some((value) => value.toLowerCase().includes(query));
    });
  const persistedPronunciationEntries = normalizePronunciationEntries(pronunciationLibrary?.entries ?? []);
  const savedPronunciationEntries = persistedPronunciationEntries;
  const draftPronunciationEntries = normalizePronunciationEntries(pronunciationEntries);
  const pronunciationLibraryDirty =
    JSON.stringify(draftPronunciationEntries) !== JSON.stringify(persistedPronunciationEntries);
  const hasIncompletePronunciationRow = pronunciationEntries.some((entry) => {
    const hasAnyValue = entry.text.trim().length > 0 || entry.alias.trim().length > 0;
    const hasBothValues = entry.text.trim().length > 0 && entry.alias.trim().length > 0;
    return hasAnyValue && !hasBothValues;
  });

  const updateVoiceSettings = (updates: Partial<VoiceSettings> | null) => {
    setFormData((prev) => {
      const current = prev.voice_settings ?? {
        voice_id: '',
        speed: 0.9,
        emotion: 'confident',
      };

      if (updates === null) {
        return { ...prev, voice_settings: null };
      }

      const nextVoiceSettings: VoiceSettings = {
        ...current,
        ...updates,
      };

      if (!nextVoiceSettings.voice_id) {
        return { ...prev, voice_settings: null };
      }

      return { ...prev, voice_settings: nextVoiceSettings };
    });
  };

  const addPronunciationEntry = () => {
    if (pronunciationEntries.length >= MAX_PRONUNCIATION_ENTRIES) {
      toast({
        title: 'Entry limit reached',
        description: `You can add up to ${MAX_PRONUNCIATION_ENTRIES} pronunciation entries.`,
        variant: 'destructive',
      });
      return;
    }
    setPronunciationEntries((prev) => [...prev, { text: '', alias: '' }]);
  };

  const removePronunciationEntry = (index: number) => {
    setPronunciationEntries((prev) => prev.filter((_, currentIndex) => currentIndex !== index));
  };

  const updatePronunciationEntry = (
    index: number,
    field: keyof PronunciationLibraryEntry,
    value: string
  ) => {
    setPronunciationEntries((prev) =>
      prev.map((entry, currentIndex) =>
        currentIndex === index ? { ...entry, [field]: value } : entry
      )
    );
  };

  const savePronunciationLibrary = () => {
    const normalizedEntries = normalizePronunciationEntries(pronunciationEntries);

    const incompleteEntry = normalizedEntries.find((entry) => !entry.text || !entry.alias);
    if (incompleteEntry) {
      toast({
        title: 'Finish each pronunciation row',
        description: 'Each entry needs both a word/phrase and a pronunciation value.',
        variant: 'destructive',
      });
      return;
    }

    const uniqueTexts = new Set<string>();
    for (const entry of normalizedEntries) {
      const key = entry.text.toLowerCase();
      if (uniqueTexts.has(key)) {
        toast({
          title: 'Duplicate word/phrase',
          description: `"${entry.text}" appears more than once. Keep one row per word/phrase.`,
          variant: 'destructive',
        });
        return;
      }
      uniqueTexts.add(key);
    }

    savePronunciationLibraryMutation.mutate(normalizedEntries);
  };

  useEffect(() => {
    setVoicePage(1);
  }, [voiceSearch, voiceFilter]);

  useEffect(() => {
    if (voicePage > totalVoicePages) {
      setVoicePage(totalVoicePages);
    }
  }, [voicePage, totalVoicePages]);

  const selectVoice = (voice: CartesiaVoice) => {
    if (previewAudio) {
      previewAudio.pause();
      setActivePreviewVoiceId(null);
    }
    updateVoiceSettings({
      voice_id: voice.id,
    });
  };

  const handleVoicePreview = async (voice: CartesiaVoice) => {
    if (activePreviewVoiceId === voice.id && previewAudio) {
      previewAudio.pause();
      setActivePreviewVoiceId(null);
      return;
    }

    try {
      setPreviewingVoiceId(voice.id);
      if (previewAudio) {
        previewAudio.pause();
      }

      const authToken = apiClient.getAuthToken();
      const previewParams = new URLSearchParams({
        speed: selectedSpeed.toFixed(2),
        emotion: selectedEmotion,
      });
      if (orgId) {
        previewParams.set('organization_id', orgId);
      }
      const response = await fetch(
        `${apiClient.getBaseUrl()}/api/integrations/cartesia/voices/${voice.id}/preview?${previewParams.toString()}`,
        {
          headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
        }
      );

      if (!response.ok) {
        throw new Error(`Preview failed with status ${response.status}`);
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const audio = new Audio(objectUrl);
      audio.onended = () => {
        URL.revokeObjectURL(objectUrl);
        setActivePreviewVoiceId((current) => (current === voice.id ? null : current));
      };

      audio.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        setActivePreviewVoiceId(null);
      };

      setPreviewAudio(audio);
      setActivePreviewVoiceId(voice.id);
      await audio.play();
    } catch (err) {
      toast({
        title: 'Preview unavailable',
        description: err instanceof Error ? err.message : 'Unable to play this voice preview right now.',
        variant: 'destructive',
      });
      setActivePreviewVoiceId(null);
    } finally {
      setPreviewingVoiceId(null);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!salesAgent) {
      toast({
        title: 'Agent not ready',
        description: 'Create an agent from Versions before making changes here.',
        variant: 'destructive',
      });
      return;
    }

    if (formData.voice_settings?.emotion?.trim() && !VALID_VOICE_EMOTIONS.has(formData.voice_settings.emotion.trim().toLowerCase())) {
      toast({
        title: 'Unsupported speaking style',
        description: 'Use one of the supported voice styles for now, or pick from the suggested options below.',
        variant: 'destructive',
      });
      return;
    }

    const payload: SalesAgentUpdate = {
      is_active: formData.is_active ?? true,
      conversation_control_mode: formData.conversation_control_mode || 'balanced',
      use_active_deck: formData.use_active_deck !== false,
      use_all_videos: formData.use_all_videos !== false,
      use_documentation: formData.use_documentation === true,
      contact_email: formData.contact_email || '',
      calendar_link: formData.calendar_link || '',
      suggestion_instructions: formData.suggestion_instructions || '',
      auto_suggestions_enabled: formData.auto_suggestions_enabled !== false,
      entry_modal_logo_url: formData.entry_modal_logo_url || '',
      header_logo_url: formData.header_logo_url || '',
      entry_modal_title: formData.entry_modal_title || '',
      entry_modal_description: formData.entry_modal_description || '',
      entry_modal_button_text: formData.entry_modal_button_text || '',
      lead_capture_name: formData.lead_capture_name || 'hidden',
      lead_capture_email: formData.lead_capture_email || 'required',
      lead_capture_phone: formData.lead_capture_phone || 'hidden',
      lead_capture_business: formData.lead_capture_business || 'hidden',
      background_screenshot_url: formData.background_screenshot_url || '',
      sticky_trigger_icon_url: formData.sticky_trigger_icon_url || '',
      sticky_trigger_text: formData.sticky_trigger_text || '',
      sticky_trigger_bg_color: formData.sticky_trigger_bg_color || '',
      sticky_trigger_text_color: '',
      sticky_trigger_font_family: formData.sticky_trigger_font_family || null,
      sticky_trigger_placement: null,
      sticky_trigger_thumbnail_url: formData.sticky_trigger_thumbnail_url || '',
      launcher_icon_bg_color: formData.launcher_icon_bg_color || '',
      voice_settings: formData.voice_settings?.voice_id
        ? {
            voice_id: formData.voice_settings.voice_id,
            speed: formData.voice_settings.speed ?? 0.9,
            emotion: formData.voice_settings.emotion?.trim() ? formData.voice_settings.emotion.trim().toLowerCase() : 'confident',
          }
        : null,
      terminology_bank: formData.terminology_bank || null,
    };

    updateMutation.mutate(payload);
  };

  const handleImageUpload = async (
    file: File,
    field: 'entry_modal_logo_url' | 'header_logo_url' | 'background_screenshot_url' | 'sticky_trigger_icon_url' | 'sticky_trigger_thumbnail_url',
    fileType: string,
    setUploading: (v: boolean) => void,
  ) => {
    if (!orgId) return;
    const allowedTypes = ['image/png', 'image/jpeg', 'image/webp', 'image/gif', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      toast({ title: 'Invalid file type', description: 'Allowed formats: PNG, JPG, WebP, GIF, SVG', variant: 'destructive' });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: 'File too large', description: 'Maximum file size is 5 MB', variant: 'destructive' });
      return;
    }
    setUploading(true);
    try {
      const formPayload = new FormData();
      formPayload.append('organizationId', orgId);
      formPayload.append('fileType', fileType);
      formPayload.append('file', file);
      if (salesAgent?.id) formPayload.append('agentKey', salesAgent.id);
      const res = await apiClient.post<FileUploadResponse>('/api/storage/upload', formPayload);
      if (!res.success || !res.data?.url) {
        throw new Error(res.error || 'Upload failed');
      }
      setFormData((prev) => ({ ...prev, [field]: res.data!.url }));
    } catch (err) {
      toast({
        title: 'Upload failed',
        description: err instanceof Error ? err.message : 'Failed to upload image',
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
    }
  };

  if (isLoading || !currentOrganization) {
    return (
      <div className="flex min-h-0 flex-col">
        <div className="flex items-center justify-center h-full">
          <div className="text-center text-subtle">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
            <p>Loading sales agent...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!isViewingVersion && !salesAgent) {
    return (
      <div className="flex-1">
        <div className="px-4 sm:px-6 md:px-8 pb-8 pt-4">
          <div className="max-w-3xl overflow-hidden rounded-xl border border-border bg-background shadow-sm">
            <div className="border-b border-border px-6 py-5">
              <h2 className="text-2xl font-semibold tracking-tight text-heading">Agent</h2>
              <p className="mt-2 max-w-2xl text-sm leading-relaxed text-muted-foreground">
                Manage how your agent runs and what visitors see before a session starts.
              </p>
            </div>

            <div className="px-6 py-6">
              <div className="grid gap-5 rounded-2xl border border-border bg-card p-5 md:grid-cols-[minmax(0,1fr)_auto] md:items-center">
                <div className="space-y-1.5">
                  <p className="text-sm font-medium text-heading">No agent yet</p>
                  <p className="text-sm text-muted-foreground">Create your agent to start running sessions.</p>
                </div>
                <div className="flex flex-wrap gap-3">
                  <Button
                    type="button"
                    onClick={() => navigate('/agents/create-version')}
                  >
                    <Sparkles className="mr-2 h-4 w-4" />
                    Create Agent
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate('/agents')}
                  >
                    Back to Agent Versions
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="flex-1">
        <div className="px-4 sm:px-6 md:px-8 pb-8 pt-4">
          <Tabs
            value={activeEditSection}
            onValueChange={(value) => {
              setActiveEditSection(value as ConfigSection);
              if (isMobile) {
                setMobileSectionView('detail');
              }
            }}
            className="flex flex-col h-full"
          >
            <div className={cn("flex flex-col gap-5", !isViewingVersion && "xl:grid xl:grid-cols-[260px_minmax(0,1fr)]")}>
              {/* Sidebar Navigation */}
              {!isViewingVersion && (!isMobile || mobileSectionView === 'list') && (
                <aside className="w-full shrink-0 xl:sticky xl:top-4 xl:self-start">
                  <div className="overflow-hidden rounded-lg border border-border/80 bg-card">
                    <div className="border-b border-border/80 px-3.5 py-3.5">
                      <h2 className="text-sm font-semibold tracking-tight text-heading">Agent</h2>
                      <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                        Manage how your agent runs and what visitors see before a session starts.
                      </p>
                    </div>

                    <div className="p-2.5">
                      {isEditing ? (
                        <nav className="flex flex-col gap-1">
                          {CONFIG_SECTIONS.map((section) => (
                            <button
                              key={section.key}
                              type="button"
                              className={workspaceRailButtonClass(activeEditSection === section.key)}
                              onClick={() => setActiveEditSection(section.key)}
                            >
                              <div className="flex items-start gap-3">
                                <section.icon className={cn(
                                  'mt-0.5 h-4 w-4 shrink-0',
                                  activeEditSection === section.key ? 'text-foreground' : 'text-muted-foreground'
                                )} />
                                <div className="min-w-0">
                                  <p className={cn(
                                    'text-sm font-medium leading-5',
                                    activeEditSection === section.key ? 'text-foreground' : 'text-muted-foreground'
                                  )}>
                                    {section.label}
                                  </p>
                                  <p className="mt-0.5 text-xs leading-snug text-muted-foreground">{section.description}</p>
                                </div>
                              </div>
                            </button>
                          ))}
                        </nav>
                      ) : (
                        <nav className="flex flex-col gap-1">
                          {CONFIG_SECTIONS.map((section) => (
                            <button
                              key={section.key}
                              type="button"
                              className={sectionButtonClass(section.key)}
                              onClick={() => selectViewSection(section.key)}
                            >
                              <div className="flex items-start gap-3">
                                <section.icon className={cn(
                                  'mt-0.5 h-4 w-4 shrink-0',
                                  activeViewSection === section.key ? 'text-foreground' : 'text-muted-foreground'
                                )} />
                                <div className="min-w-0">
                                  <p className={cn(
                                    'text-sm font-medium leading-5',
                                    activeViewSection === section.key ? 'text-foreground' : 'text-muted-foreground'
                                  )}>
                                    {section.label}
                                  </p>
                                  <p className="mt-0.5 text-xs leading-snug text-muted-foreground">{section.description}</p>
                                </div>
                              </div>
                            </button>
                          ))}
                        </nav>
                      )}
                    </div>

                    {!isEditing && (
                      <div className="border-t border-border/80 px-3.5 py-3.5">
                        <p className="text-xs font-medium text-muted-foreground">Preview</p>
                        <div className="mt-3 grid grid-cols-2 gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            disabled={!liveDemoUrl}
                            onClick={() => liveDemoUrl && window.open(liveDemoUrl, '_blank')}
                            className="flex-1 justify-center gap-1.5 h-8 text-xs rounded-md border-[hsl(var(--input))] bg-background text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                          >
                            Full Page
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            disabled={!embeddedDemoUrl}
                            onClick={() => embeddedDemoUrl && window.open(embeddedDemoUrl, '_blank')}
                            className="flex-1 justify-center gap-1.5 h-8 text-xs rounded-md border-[hsl(var(--input))] bg-background text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                          >
                            Embedded
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            disabled={!launcherDemoUrl}
                            onClick={() => launcherDemoUrl && window.open(launcherDemoUrl, '_blank')}
                            className="col-span-2 justify-center gap-1.5 h-8 text-xs rounded-md border-[hsl(var(--input))] bg-background text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                          >
                            Launcher
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </aside>
              )}

              {/* Main Content Area */}
              {(!isMobile || mobileSectionView === 'detail') && (
                <main className="min-w-0 overflow-hidden rounded-lg border border-border/80 bg-card">
                  {isMobile && (
                    <div className="border-b border-border/80 px-4 py-3 flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setMobileSectionView('list')}
                      >
                        <ChevronLeft className="h-4 w-4 mr-1" />
                        Back
                      </Button>
                      <span className="text-sm text-muted-foreground">Agent</span>
                    </div>
                  )}
                  {!isViewingVersion && (
                    <div className="border-b border-border/80 px-5 py-4 lg:px-6">
                      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                        <div className="space-y-1.5">
                          {isEditing && (
                            <div className="flex flex-wrap items-center gap-2">
                              <Badge variant="outline" className="rounded-full px-2.5 py-0.5 text-[10px] uppercase tracking-[0.16em]">
                                Editing
                              </Badge>
                            </div>
                          )}
                          <div>
                            <h2 className="text-lg font-semibold leading-6 tracking-tight text-heading">{currentSectionMeta.label}</h2>
                            <p className="mt-1 max-w-2xl text-sm leading-relaxed text-muted-foreground">
                              {currentSectionMeta.description}
                            </p>
                          </div>
                        </div>

                        <div className="flex flex-wrap items-center gap-2">
                          {isEditing ? (
                            <>
                              <Button
                                type="button"
                                variant="outline"
                                className="h-8 rounded-md"
                                onClick={() => {
                                  setIsEditing(false);
                                  setFormData(mapSalesAgentToFormData(salesAgent));
                                }}
                              >
                                Cancel
                              </Button>
                              <Button
                                type="submit"
                                form="agent-form"
                                disabled={updateMutation.isPending}
                                className="h-8 rounded-md"
                              >
                                {updateMutation.isPending ? (
                                  <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Saving
                                  </>
                                ) : (
                                  <>
                                    <Save className="mr-2 h-4 w-4" />
                                    Save Changes
                                  </>
                                )}
                              </Button>
                            </>
                          ) : (
                            <Button
                              type="button"
                              onClick={() => {
                                setFormData(mapSalesAgentToFormData(salesAgent));
                                setIsEditing(true);
                                setActiveEditSection(activeViewSection);
                              }}
                              className="h-8 rounded-md"
                            >
                              <Edit className="mr-2 h-4 w-4" />
                              Edit
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                {isViewingVersion ? (
                  <div className="p-6 space-y-4">
                    {resolvedSelectedVersion !== null && compareDropdownOptions.length > 0 && (
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="text-sm text-muted-foreground whitespace-nowrap">
                          Comparing v{resolvedSelectedVersion} against:
                        </span>
                        <Select
                          value={compareTargetVersion !== null ? String(compareTargetVersion) : undefined}
                          onValueChange={handleCompareTargetChange}
                        >
                          <SelectTrigger className="w-[180px] h-9">
                            <SelectValue placeholder="Select version" />
                          </SelectTrigger>
                          <SelectContent>
                            {compareDropdownOptions.map((v) => (
                              <SelectItem key={v.version} value={String(v.version)}>
                                v{v.version}
                                {v.status === 'live' ? ' (Live)' : v.status === 'staging' ? ' (Staging)' : ''}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    {isVersionLoading ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                      </div>
                    ) : versionDiff ? (
                      <PromptDiffView
                        data={versionDiff}
                        baseVersion={baseVersionMetadata}
                        compareVersion={compareVersionMetadata}
                      />
                    ) : (
                      <p className="text-sm text-muted-foreground italic">Unable to load version diff.</p>
                    )}
                  </div>
                ) : salesAgent && !isEditing ? (
                  <div className="px-5 py-6 lg:px-6">
                    {activeViewSection === 'general' && (
      <section className="max-w-4xl divide-y divide-border/60">
        <div className="grid gap-4 py-5 md:grid-cols-[220px_minmax(0,1fr)_auto] md:items-center">
                          <div>
                            <p className="text-sm font-medium text-heading">Active Status</p>
                            <p className="mt-1 text-sm text-muted-foreground">Enable or disable the sales agent for production use.</p>
                          </div>
                          <p className="text-sm text-muted-foreground md:justify-self-start">
                            {salesAgent.is_active ? 'The agent is currently available for production sessions.' : 'The agent is currently paused.'}
                          </p>
                          <Switch
                            checked={salesAgent.is_active}
                            onCheckedChange={() => {}}
                            className="data-[state=checked]:bg-primary disabled:opacity-100"
                            disabled
                          />
                        </div>

                        <div className="grid gap-4 py-5 md:grid-cols-[220px_minmax(0,1fr)]">
                          <div>
                            <p className="text-sm font-medium text-heading">Contact Email</p>
                            <p className="mt-1 text-sm text-muted-foreground">Used for follow-up and contact routing.</p>
                          </div>
                          <div className="rounded-2xl border border-border bg-card px-4 py-3 text-sm text-heading">
                            {salesAgent.contact_email || 'No contact email configured'}
                          </div>
                        </div>
                      </section>
                    )}

                    {activeViewSection === 'voice' && (
                      <section className="grid max-w-5xl gap-6 lg:grid-cols-[minmax(0,1fr)_280px]">
                        <div className="divide-y divide-border/60">
                          <div className="grid gap-4 py-5 md:grid-cols-[220px_minmax(0,1fr)]">
                            <div>
                              <p className="text-sm font-medium text-heading">Selected Voice</p>
                              <p className="mt-1 text-sm text-muted-foreground">Primary voice used for live sessions.</p>
                            </div>
                            <div className="text-sm text-heading">{savedVoiceLabel}</div>
                          </div>
                          <div className="grid gap-4 py-5 md:grid-cols-[220px_minmax(0,1fr)]">
                            <div>
                              <p className="text-sm font-medium text-heading">Speaking Style</p>
                              <p className="mt-1 text-sm text-muted-foreground">Emotion profile applied to playback.</p>
                            </div>
                            <div className="text-sm text-heading capitalize">{salesAgent.voice_settings?.emotion || 'confident'}</div>
                          </div>
                          <div className="grid gap-4 py-5 md:grid-cols-[220px_minmax(0,1fr)]">
                            <div>
                              <p className="text-sm font-medium text-heading">Speed</p>
                              <p className="mt-1 text-sm text-muted-foreground">Cadence used for voice responses.</p>
                            </div>
                            <div className="text-sm text-heading">
                              {(salesAgent.voice_settings?.speed ?? 0.9).toFixed(2)} · {describeVoiceSpeed(salesAgent.voice_settings?.speed ?? 0.9)}
                            </div>
                          </div>
                          <div className="grid gap-4 py-5 md:grid-cols-[220px_minmax(0,1fr)]">
                            <div>
                              <p className="text-sm font-medium text-heading">Voice Details</p>
                              <p className="mt-1 text-sm text-muted-foreground">Catalog metadata for the current voice.</p>
                            </div>
                            <div className="space-y-2 text-sm text-body">
                              <p><span className="font-medium">Voice ID:</span> <span className="break-all">{salesAgent.voice_settings?.voice_id || 'Not configured'}</span></p>
                              {savedVoiceLanguage && (
                                <p><span className="font-medium">Language:</span> {savedVoiceLanguage}</p>
                              )}
                            </div>
                          </div>

                          <div className="overflow-hidden rounded-lg border card-border bg-background/80">
                            <div className="border-b border-border/70 bg-gray-50/40 px-4 py-3 dark:bg-zinc-900/30">
                              <div className="flex items-center justify-between gap-2">
                                <Label className="text-sm font-semibold">Pronunciation Library</Label>
                                <Badge variant="secondary" className="text-[10px] uppercase tracking-wide">
                                  {savedPronunciationEntries.length} {savedPronunciationEntries.length === 1 ? 'entry' : 'entries'}
                                </Badge>
                              </div>
                            </div>

                            <div className="px-4 py-4">
                              {isPronunciationLibraryLoading ? (
                                <div className="flex items-center justify-center rounded-lg border border-dashed border-border bg-background/40 py-8 text-muted-foreground">
                                  <Loader2 className="h-5 w-5 animate-spin" />
                                </div>
                              ) : savedPronunciationEntries.length === 0 ? (
                                <div className="rounded-lg border border-dashed border-border bg-background/40 p-4">
                                  <p className="text-sm text-subtle">No pronunciation entries configured for this organization.</p>
                                </div>
                              ) : (
                                <div className="overflow-hidden rounded-lg border border-border/70">
                                  <div className="hidden grid-cols-[minmax(0,1fr)_minmax(0,1fr)] border-b border-border/70 bg-gray-50/60 px-3 py-2 text-xs font-medium text-subtle dark:bg-zinc-900/50 md:grid">
                                    <span>Word or phrase</span>
                                    <span>Pronunciation</span>
                                  </div>
                                  <div className="divide-y divide-border/60">
                                    {savedPronunciationEntries.map((entry, index) => (
                                      <div
                                        key={`saved-pronunciation-row-${index}`}
                                        className="grid grid-cols-1 gap-2 px-3 py-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] md:gap-3"
                                      >
                                        <div>
                                          <Label className="text-[11px] text-subtle md:hidden">Word or phrase</Label>
                                          <p className="text-sm text-body">{entry.text}</p>
                                        </div>
                                        <div>
                                          <Label className="text-[11px] text-subtle md:hidden">Pronunciation</Label>
                                          <p className="text-sm text-body font-mono">{entry.alias}</p>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="rounded-xl border border-border bg-card p-5">
                          <p className="text-xs font-medium text-muted-foreground">Preview</p>
                          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                            Hear the current configuration exactly as it will sound in live voice sessions.
                          </p>
                          <div className="mt-5 rounded-2xl border border-border bg-background px-4 py-3">
                            <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Voice ID</p>
                            <p className="mt-2 break-all text-sm text-heading">{salesAgent.voice_settings?.voice_id || 'Not configured'}</p>
                          </div>

                          {savedVoice && (
                            <Button
                              type="button"
                              variant="outline"
                              className="mt-5 w-full gap-2 rounded-xl"
                              onClick={() => handleVoicePreview(savedVoice)}
                              disabled={previewingVoiceId === savedVoice.id}
                            >
                              {previewingVoiceId === savedVoice.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : activePreviewVoiceId === savedVoice.id ? (
                                <Pause className="h-4 w-4" />
                              ) : (
                                <Play className="h-4 w-4" />
                              )}
                              {activePreviewVoiceId === savedVoice.id ? 'Pause Preview' : 'Play Preview'}
                            </Button>
                          )}
                        </div>
                      </section>
                    )}

                    {/* Content Section */}
                    {activeViewSection === 'content' && (
                      <section className="max-w-4xl divide-y divide-border/60">
                        <div className="grid gap-4 py-5 md:grid-cols-[220px_minmax(0,1fr)_auto] md:items-center">
                          <div>
                            <p className="text-sm font-medium text-heading">Active Presentation Deck</p>
                            <p className="mt-1 text-sm text-muted-foreground">Allow the agent to reference the active slide deck.</p>
                          </div>
                          <p className="text-sm text-muted-foreground">Presentation content is {salesAgent.use_active_deck ? 'available' : 'excluded'} during sessions.</p>
                          <Badge variant={salesAgent.use_active_deck ? "default" : "secondary"}>
                            {salesAgent.use_active_deck ? "Enabled" : "Disabled"}
                          </Badge>
                        </div>
                        <div className="grid gap-4 py-5 md:grid-cols-[220px_minmax(0,1fr)_auto] md:items-center">
                          <div>
                            <p className="text-sm font-medium text-heading">All Organization Videos</p>
                            <p className="mt-1 text-sm text-muted-foreground">Allow the agent to search video content.</p>
                          </div>
                          <p className="text-sm text-muted-foreground">Video assets are {salesAgent.use_all_videos ? 'available' : 'excluded'} in the live experience.</p>
                          <Badge variant={salesAgent.use_all_videos ? "default" : "secondary"}>
                            {salesAgent.use_all_videos ? "Enabled" : "Disabled"}
                          </Badge>
                        </div>
                        <div className="grid gap-4 py-5 md:grid-cols-[220px_minmax(0,1fr)_auto] md:items-center">
                          <div>
                            <p className="text-sm font-medium text-heading">Documentation</p>
                            <p className="mt-1 text-sm text-muted-foreground">Allow the agent to show documentation during sessions.</p>
                          </div>
                          <p className="text-sm text-muted-foreground">Documentation pages are {salesAgent.use_documentation ? 'available' : 'excluded'} in the live experience.</p>
                          <Badge variant={salesAgent.use_documentation ? "default" : "secondary"}>
                            {salesAgent.use_documentation ? "Enabled" : "Disabled"}
                          </Badge>
                        </div>
                      </section>
                    )}

                    {activeViewSection === 'conversation' && (
                      <section className="max-w-4xl divide-y divide-border/60">
                        <div className="grid gap-4 py-5 md:grid-cols-[220px_minmax(0,1fr)_auto] md:items-center">
                          <div>
                            <p className="text-sm font-medium text-heading">Conversation Flow</p>
                            <p className="mt-1 text-sm text-muted-foreground">How strongly the agent should lead the demo once discovery is complete.</p>
                          </div>
                          <p className="text-sm text-muted-foreground">{conversationModeOption.description}</p>
                          <Badge variant="secondary">
                            {conversationModeOption.label}
                          </Badge>
                        </div>
                        <div className="grid gap-4 py-5 md:grid-cols-[220px_minmax(0,1fr)_auto] md:items-center">
                          <div>
                            <p className="text-sm font-medium text-heading">Auto-Suggestions</p>
                            <p className="mt-1 text-sm text-muted-foreground">Show follow-up suggestion pills to visitors during live sessions.</p>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {salesAgent.auto_suggestions_enabled ? 'Suggestion chips are shown after each agent response.' : 'Suggestion chips are hidden during sessions.'}
                          </p>
                          <Badge variant={salesAgent.auto_suggestions_enabled ? "default" : "secondary"}>
                            {salesAgent.auto_suggestions_enabled ? "Enabled" : "Disabled"}
                          </Badge>
                        </div>
                        {salesAgent.auto_suggestions_enabled && (
                        <div className="grid gap-4 py-5 md:grid-cols-[220px_minmax(0,1fr)]">
                          <div>
                            <p className="text-sm font-medium text-heading">Suggestion Guidance</p>
                            <p className="mt-1 text-sm text-muted-foreground">Custom instructions for the follow-up suggestion chips shown during the demo.</p>
                          </div>
                          <div className="rounded-2xl border border-border bg-card px-4 py-3 text-sm text-heading whitespace-pre-wrap">
                            {salesAgent.suggestion_instructions || 'No custom suggestion guidance configured. The default suggestion behavior will be used.'}
                          </div>
                        </div>
                        )}
                      </section>
                    )}

                    {activeViewSection === 'calendar' && (
                      <section className="max-w-4xl divide-y divide-border/60">
                        <div className="grid gap-4 py-5 md:grid-cols-[220px_minmax(0,1fr)]">
                          <div>
                            <p className="text-sm font-medium text-heading">Calendar Link</p>
                            <p className="mt-1 text-sm text-muted-foreground">Booking destination for follow-up meetings.</p>
                          </div>
                          <div className="rounded-2xl border border-border bg-card px-4 py-3 text-sm text-heading">
                            {salesAgent.calendar_link || 'Using Hobbes default (cal.com/hobbes)'}
                          </div>
                        </div>
                      </section>
                    )}

                    {/* Entry Modal Section */}
                    {activeViewSection === 'entryModal' && (
                      <section className="max-w-none">
                        <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_320px]">
                          <div className="space-y-6">
                            <div className="rounded-xl border border-border bg-card p-5">
                              <div className="flex items-center justify-between gap-4">
                                <div>
                                  <Label className="text-xs font-medium text-muted-foreground">Cover wordmark</Label>
                                  <p className="mt-2 text-sm text-muted-foreground">Wordmark overlaid on top of the entry modal cover image.</p>
                                </div>
                                <div className="shrink-0">
                                  {salesAgent.entry_modal_logo_url ? (
                                    <div className="inline-block rounded-2xl" style={{ backgroundImage: 'repeating-conic-gradient(#d1d5db 0% 25%, transparent 0% 50%)', backgroundSize: '16px 16px' }}>
                                      <img
                                        src={salesAgent.entry_modal_logo_url}
                                        alt="Entry modal cover wordmark"
                                        className="h-12 w-auto rounded-2xl object-contain"
                                      />
                                    </div>
                                  ) : (
                                    <p className="text-sm text-muted-foreground">Default logo</p>
                                  )}
                                </div>
                              </div>
                            </div>

                            <div className="rounded-xl border border-border bg-card p-5">
                              <div className="flex items-center justify-between gap-4">
                                <div>
                                  <Label className="text-xs font-medium text-muted-foreground">Header wordmark</Label>
                                  <p className="mt-2 text-sm text-muted-foreground">Light wordmark shown in the top-left of the widget launcher header. Falls back to the cover wordmark when empty.</p>
                                </div>
                                <div className="shrink-0">
                                  {salesAgent.header_logo_url ? (
                                    <div className="inline-block rounded-2xl bg-zinc-900">
                                      <img
                                        src={salesAgent.header_logo_url}
                                        alt="Widget header wordmark"
                                        className="h-12 w-auto rounded-2xl object-contain"
                                      />
                                    </div>
                                  ) : (
                                    <p className="text-sm text-muted-foreground">Falls back to cover wordmark</p>
                                  )}
                                </div>
                              </div>
                            </div>

                            <div className="rounded-xl border border-border bg-background">
                              <div className="grid gap-4 px-5 py-5 md:grid-cols-2 md:items-start">
                                <div>
                                  <p className="text-xs font-medium text-muted-foreground">Title</p>
                                  <p className="mt-3 text-sm font-medium text-heading">{salesAgent.entry_modal_title || 'Experience Hobbes'}</p>
                                </div>
                                <div>
                                  <p className="text-xs font-medium text-muted-foreground">Button Text</p>
                                  <p className="mt-3 text-sm font-medium text-heading">{salesAgent.entry_modal_button_text || 'Start Your Personalized Demo'}</p>
                                </div>
                              </div>
                              <div className="border-t border-border px-5 py-5">
                                <p className="text-xs font-medium text-muted-foreground">Description</p>
                                <p className="mt-3 text-sm leading-relaxed text-heading">
                                  {salesAgent.entry_modal_description || 'Discover how a Hobbes agent can transform your revenue engine.'}
                                </p>
                              </div>
                            </div>
                          </div>

                          <div className="rounded-xl border border-border bg-card p-5">
                            <div>
                              <Label className="text-xs font-medium text-muted-foreground">Contact Fields</Label>
                              <p className="mt-2 text-sm text-muted-foreground">Choose what visitors are asked for before the demo starts.</p>
                            </div>

                            <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-1">
                              {[
                                { label: 'Name', value: salesAgent.lead_capture_name },
                                { label: 'Email', value: salesAgent.lead_capture_email },
                                { label: 'Phone', value: salesAgent.lead_capture_phone },
                                { label: 'Company', value: salesAgent.lead_capture_business },
                              ].map((field) => (
                                <div key={field.label} className="rounded-xl border border-border bg-background px-4 py-3">
                                  <p className="text-sm font-medium text-heading">{field.label}</p>
                                  <p className="mt-1 text-sm text-muted-foreground">{getLeadCaptureStateLabel(field.value)}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </section>
                    )}

                    {/* Launcher Trigger Section */}
                    {activeViewSection === 'launcherTrigger' && (
                      <section className="max-w-none space-y-6">
                        <div className="grid gap-4 lg:grid-cols-2">
                            <div className="rounded-xl border border-border bg-card p-5">
                              <div className="flex items-center justify-between gap-4">
                                <div>
                                  <Label className="text-xs font-medium text-muted-foreground">Trigger Icon</Label>
                                  <p className="mt-2 text-sm text-muted-foreground">Square icon displayed as a circular avatar in the trigger pill.</p>
                                </div>
                                <div className="shrink-0">
                                  {salesAgent.sticky_trigger_icon_url ? (
                                    <div className="inline-block rounded-full" style={{ backgroundImage: 'repeating-conic-gradient(#d1d5db 0% 25%, transparent 0% 50%)', backgroundSize: '16px 16px' }}>
                                      <img
                                        src={salesAgent.sticky_trigger_icon_url}
                                        alt="Launcher trigger icon"
                                        className="h-10 w-10 rounded-full object-cover"
                                      />
                                    </div>
                                  ) : (
                                    <p className="text-sm text-muted-foreground">No icon</p>
                                  )}
                                </div>
                              </div>
                            </div>

                            <div className="rounded-xl border border-border bg-background">
                              <div className="grid gap-4 px-5 py-5 md:grid-cols-4 md:items-start">
                                <div>
                                  <p className="text-xs font-medium text-muted-foreground">Button Text</p>
                                  <p className="mt-3 text-sm font-medium text-heading">{salesAgent.sticky_trigger_text || 'Start Demo'}</p>
                                </div>
                                <div>
                                  <p className="text-xs font-medium text-muted-foreground">Font Family</p>
                                  <p className="mt-3 text-sm font-medium text-heading">{salesAgent.sticky_trigger_font_family || 'Default (system font)'}</p>
                                </div>
                                <div>
                                  <p className="text-xs font-medium text-muted-foreground">Tint</p>
                                  <div className="mt-3 flex items-center gap-2 text-sm font-medium text-heading">
                                    <span
                                      aria-hidden
                                      className="h-3.5 w-3.5 rounded-full border border-border"
                                      style={{ backgroundColor: normalizeHexColor(salesAgent.sticky_trigger_bg_color) || DEFAULT_LAUNCHER_TINT }}
                                    />
                                    <span>{normalizeHexColor(salesAgent.sticky_trigger_bg_color) || 'Default'}</span>
                                  </div>
                                </div>
                                <div>
                                  <p className="text-xs font-medium text-muted-foreground">Icon background color</p>
                                  <div className="mt-3 flex items-center gap-2 text-sm font-medium text-heading">
                                    <span
                                      aria-hidden
                                      className="h-3.5 w-3.5 rounded-full border border-border"
                                      style={{ backgroundColor: normalizeHexColor(salesAgent.launcher_icon_bg_color) || DEFAULT_LAUNCHER_ICON_BG }}
                                    />
                                    <span>{normalizeHexColor(salesAgent.launcher_icon_bg_color) || 'Default'}</span>
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="rounded-xl border border-border bg-card p-5">
                              <p className="text-xs font-medium text-muted-foreground">Hover Thumbnail</p>
                              <div className="mt-3">
                                {salesAgent.sticky_trigger_thumbnail_url ? (
                                  <div
                                    className="w-full max-w-xs overflow-hidden rounded-lg border border-border"
                                    style={{ backgroundImage: 'repeating-conic-gradient(#d1d5db 0% 25%, transparent 0% 50%)', backgroundSize: '16px 16px' }}
                                  >
                                    <div className="aspect-video w-full">
                                      <img
                                        src={salesAgent.sticky_trigger_thumbnail_url}
                                        alt="Launcher hover thumbnail"
                                        className="h-full w-full object-cover"
                                      />
                                    </div>
                                  </div>
                                ) : (
                                  <p className="text-sm text-muted-foreground">No hover thumbnail</p>
                                )}
                              </div>
                            </div>
                        </div>

                        <div className="rounded-xl border border-border bg-card p-5">
                          <Label className="text-xs font-medium text-muted-foreground">Preview</Label>
                          <p className="mt-2 text-sm text-muted-foreground">
                            {salesAgent.sticky_trigger_thumbnail_url
                              ? 'Switch between icon, rest, and open states. The thumbnail appears with the open launcher.'
                              : 'Switch between icon, rest, and open states from the latest launcher.'}
                          </p>
                          {salesAgent.sticky_trigger_font_family && (
                            <link
                              rel="stylesheet"
                              href={`https://fonts.googleapis.com/css2?family=${encodeURIComponent(salesAgent.sticky_trigger_font_family)}&display=swap`}
                            />
                          )}
                          <div className="mt-5 relative h-[460px] w-full overflow-hidden rounded-xl border border-border bg-background">
                            <StickyTriggerMorphPreview
                              placement={DEFAULT_STICKY_TRIGGER_PLACEMENT}
                              iconUrl={salesAgent.sticky_trigger_icon_url || ''}
                              text={salesAgent.sticky_trigger_text || ''}
                              tintColor={salesAgent.sticky_trigger_bg_color || null}
                              textColor={salesAgent.sticky_trigger_text_color || null}
                              fontFamily={salesAgent.sticky_trigger_font_family || null}
                              thumbnailUrl={salesAgent.sticky_trigger_thumbnail_url || ''}
                              leadCaptureEmail={salesAgent.lead_capture_email}
                              iconBgColor={salesAgent.launcher_icon_bg_color || null}
                            />
                          </div>
                        </div>
                      </section>
                    )}

                    {activeViewSection === 'launchState' && (
                      <section className="max-w-5xl space-y-8">
                        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_280px]">
                          <div className="rounded-xl border border-border bg-card p-6">
                            <div className="flex items-start justify-between gap-4">
                              <div>
                                <Label className="text-xs font-medium text-muted-foreground">Starting Image</Label>
                                <p className="mt-3 max-w-xl text-sm leading-relaxed text-muted-foreground">
                                  This image appears in the viewer before the demo content loads.
                                </p>
                              </div>
                              <Badge variant="outline" className="rounded-full px-2.5 py-1 text-[10px] uppercase tracking-[0.16em]">
                                Viewer
                              </Badge>
                            </div>

                            <div className="mt-6 flex min-h-[280px] items-center justify-center rounded-xl border border-border bg-background p-6">
                              {salesAgent.background_screenshot_url ? (
                                <div
                                  className="flex w-full items-center justify-center rounded-xl"
                                  style={{ backgroundImage: 'repeating-conic-gradient(#d1d5db 0% 25%, transparent 0% 50%)', backgroundSize: '16px 16px' }}
                                >
                                  <img
                                    src={salesAgent.background_screenshot_url}
                                    alt="Launch state preview"
                                    className="max-h-[220px] w-auto max-w-full rounded-xl object-contain"
                                  />
                                </div>
                              ) : (
                                <div className="text-center">
                                  <Image className="mx-auto h-10 w-10 text-muted-foreground/50" />
                                  <p className="mt-4 text-sm font-medium text-heading">No starting image set</p>
                                  <p className="mt-1 text-sm text-muted-foreground">The viewer will open without a custom launch image.</p>
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="rounded-xl border border-border bg-card p-5">
                            <p className="text-xs font-medium text-muted-foreground">Usage</p>
                            <div className="mt-4 space-y-4 text-sm text-muted-foreground">
                              <p>Use this for a clean branded first impression before any slides or content appear.</p>
                              <p>Best results come from landscape imagery with strong focal balance and minimal embedded text.</p>
                            </div>
                          </div>
                        </div>
                      </section>
                    )}

                    {activeViewSection === 'terminology' && (
                      <section className="max-w-4xl space-y-6">
                        {salesAgent.terminology_bank && Object.keys(salesAgent.terminology_bank).length > 0 ? (
                          Object.entries(salesAgent.terminology_bank).map(([country, entries]) => (
                            <div key={country} className="rounded-xl border border-border bg-card">
                              <div className="border-b border-border px-5 py-3">
                                <h3 className="text-sm font-semibold text-heading flex items-center gap-2">
                                  <Globe2 className="h-4 w-4 text-muted-foreground" />
                                  {country}
                                </h3>
                              </div>
                              <div className="divide-y divide-border/60">
                                {(entries as TerminologyEntry[]).map((entry, idx) => (
                                  <div key={idx} className="grid gap-2 px-5 py-3 md:grid-cols-[200px_minmax(0,1fr)]">
                                    <p className="text-sm font-medium text-heading">{entry.term}</p>
                                    <p className="text-sm text-muted-foreground">{entry.context || '—'}</p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border py-16">
                            <Languages className="h-10 w-10 text-muted-foreground/40" />
                            <p className="mt-4 text-sm font-medium text-heading">No terminology configured</p>
                            <p className="mt-1 text-sm text-muted-foreground">Click Edit to add regional terminology for your agent.</p>
                          </div>
                        )}
                      </section>
                    )}
                  </div>
                ) : (
                  <form id="agent-form" onSubmit={handleSubmit} className="h-full">
                    <div className="px-6 py-8 lg:px-8">
                      <TabsContent value="general" className="mt-0 space-y-10">
                        <div className="max-w-4xl divide-y divide-border/60">
                          <div className="grid gap-4 py-5 md:grid-cols-[220px_minmax(0,1fr)_auto] md:items-center">
                            <div>
                              <Label htmlFor="is_active" className="text-sm font-medium cursor-pointer text-heading">Active Status</Label>
                              <p className="mt-1 text-sm text-muted-foreground">Enable or disable the sales agent for production use.</p>
                            </div>
                            <p className="text-sm text-muted-foreground">Changes take effect for live demo traffic immediately after saving.</p>
                            <Switch
                              id="is_active"
                              checked={formData.is_active || false}
                              onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                            />
                          </div>
                          <div className="grid gap-4 py-5 md:grid-cols-[220px_minmax(0,1fr)]">
                            <div>
                              <Label htmlFor="contact_email" className="text-sm font-medium text-heading">Contact Email</Label>
                              <p className="mt-1 text-sm text-muted-foreground">Used for sales-agent follow-up and contact routing.</p>
                            </div>
                            <Input
                              id="contact_email"
                              type="email"
                              value={formData.contact_email || ''}
                              onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                              placeholder="team@yourcompany.com"
                              className="h-11 rounded-2xl"
                            />
                          </div>
                        </div>
                      </TabsContent>

                      <TabsContent value="voice" className="mt-0 space-y-6">
                        <div className="max-w-5xl">
                          <div className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <button
                                type="button"
                                onClick={() => setVoiceEditorPanel('voice')}
                                className={voicePanelCardClass('voice')}
                                aria-pressed={voiceEditorPanel === 'voice'}
                              >
                                <Label className="text-xs text-subtle mb-1 block">Selected Voice</Label>
                                <p className="text-sm font-medium">{selectedVoiceLabel}</p>
                              </button>
                              <button
                                type="button"
                                onClick={() => setVoiceEditorPanel('style')}
                                className={voicePanelCardClass('style')}
                                aria-pressed={voiceEditorPanel === 'style'}
                              >
                                <Label className="text-xs text-subtle mb-1 block">Speaking Style</Label>
                                <p className="text-sm font-medium capitalize">{selectedEmotion}</p>
                              </button>
                              <button
                                type="button"
                                onClick={() => setVoiceEditorPanel('speed')}
                                className={voicePanelCardClass('speed')}
                                aria-pressed={voiceEditorPanel === 'speed'}
                              >
                                <Label className="text-xs text-subtle mb-1 block">Speed</Label>
                                <p className="text-sm font-medium">{selectedSpeed.toFixed(2)} · {describeVoiceSpeed(selectedSpeed)}</p>
                              </button>
                            </div>

                            <div className="rounded-lg border card-border bg-gray-50/30 dark:bg-zinc-900/30 p-4">
                              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                                <div>
                                  <Label className="text-xs text-subtle uppercase tracking-wider font-semibold mb-1 block">Voice Preview</Label>
                                  <p className="text-sm text-body">
                                    {selectedVoice
                                      ? `Play ${selectedVoice.name} using ${selectedEmotion} at ${selectedSpeed.toFixed(2)} speed.`
                                      : 'Select a voice to preview the current speaking style and speed.'}
                                  </p>
                                </div>
                                <Button
                                  type="button"
                                  variant="outline"
                                  className="gap-2 md:self-start"
                                  onClick={() => selectedVoice && handleVoicePreview(selectedVoice)}
                                  disabled={!selectedVoice || previewingVoiceId === selectedVoice.id}
                                >
                                  {selectedVoice && previewingVoiceId === selectedVoice.id ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : selectedVoice && activePreviewVoiceId === selectedVoice.id ? (
                                    <Pause className="h-4 w-4" />
                                  ) : (
                                    <Play className="h-4 w-4" />
                                  )}
                                  {selectedVoice && activePreviewVoiceId === selectedVoice.id ? 'Pause Sample' : 'Play Sample'}
                                </Button>
                              </div>
                            </div>

                            {voiceEditorPanel === 'voice' && (
                            <div className="space-y-4">
                              <div className="flex items-center justify-between gap-3">
                                <Label className="text-sm font-semibold">Available Voices</Label>
                              </div>
                              <Tabs
                                value={voiceFilter}
                                onValueChange={(value) => setVoiceFilter(value as VoiceFilter)}
                                className="w-full"
                              >
                                <TabsList className="inline-flex h-9 w-auto">
                                  <TabsTrigger value="recommended">Recommended</TabsTrigger>
                                  <TabsTrigger value="all">All</TabsTrigger>
                                  <TabsTrigger value="feminine">Feminine</TabsTrigger>
                                  <TabsTrigger value="masculine">Masculine</TabsTrigger>
                                </TabsList>
                              </Tabs>
                              <div className="relative">
                                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                                <Input
                                  value={voiceSearch}
                                  onChange={(e) => setVoiceSearch(e.target.value)}
                                  placeholder="Search voices"
                                  className="h-10 pl-9"
                                />
                              </div>
                            <div className="grid gap-3 md:grid-cols-2">
                              {isVoiceCatalogLoading ? (
                                <div className="col-span-full flex items-center justify-center rounded-lg border border-dashed border-border bg-gray-50/30 dark:bg-zinc-900/30 py-10 text-muted-foreground">
                                  <Loader2 className="h-5 w-5 animate-spin" />
                                </div>
                              ) : filteredVoices.length === 0 ? (
                                <div className="col-span-full rounded-lg border border-dashed border-border bg-gray-50/30 dark:bg-zinc-900/30 p-6 text-center">
                                  <p className="text-sm font-medium text-heading">No voices match this filter</p>
                                  <p className="mt-1 text-sm text-subtle">Try a different search term or switch tabs.</p>
                                </div>
                              ) : (
                                paginatedVoices.map((voice) => {
                                  const isSelected = formData.voice_settings?.voice_id === voice.id;
                                  return (
                                    <div
                                      key={voice.id}
                                      role="button"
                                      tabIndex={0}
                                      onClick={() => selectVoice(voice)}
                                      onKeyDown={(event) => {
                                        if (event.key === 'Enter' || event.key === ' ') {
                                          event.preventDefault();
                                          selectVoice(voice);
                                        }
                                      }}
                                      className={cn(
                                        'rounded-lg border p-4',
                                        isSelected
                                          ? 'border-primary bg-primary/5'
                                          : 'card-border bg-gray-50/30 dark:bg-zinc-900/30'
                                      )}
                                    >
                                      <div className="space-y-2">
                                        <div className="flex flex-wrap items-center gap-2">
                                          <p className="text-sm font-semibold text-heading">{voice.name}</p>
                                          {isSelected && (
                                            <Badge className="text-[10px] uppercase tracking-wide">Selected</Badge>
                                          )}
                                        </div>
                                        <p className="text-sm text-subtle line-clamp-2">
                                          {voice.description || 'Voice preview available.'}
                                        </p>
                                      </div>

                                      <div className="mt-3 flex flex-wrap gap-2">
                                        {voice.language && (
                                          <Badge variant="outline" className="gap-1">
                                            <Globe2 className="h-3 w-3" />
                                            {voice.language}
                                          </Badge>
                                        )}
                                        {voice.gender && (
                                          <Badge variant="outline">{voice.gender}</Badge>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })
                              )}
                            </div>
                            {filteredVoices.length > 0 && (
                              <div className="flex items-center justify-between pt-1">
                                <p className="text-xs text-subtle">
                                  Showing {(voicePage - 1) * VOICES_PER_PAGE + 1}-{Math.min(voicePage * VOICES_PER_PAGE, filteredVoices.length)} of {filteredVoices.length}
                                </p>
                                <div className="flex items-center gap-2">
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setVoicePage((page) => Math.max(1, page - 1))}
                                    disabled={voicePage === 1}
                                    className="h-8 px-2"
                                  >
                                    <ChevronLeft className="h-4 w-4" />
                                  </Button>
                                  <span className="text-xs text-subtle">
                                    Page {voicePage} of {totalVoicePages}
                                  </span>
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setVoicePage((page) => Math.min(totalVoicePages, page + 1))}
                                    disabled={voicePage === totalVoicePages}
                                    className="h-8 px-2"
                                  >
                                    <ChevronRight className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>
                            )}
                            </div>
                            )}

                            {voiceEditorPanel === 'style' && (
                            <div className="space-y-3">
                              <Label className="text-sm font-semibold">Search Styles</Label>
                              <div className="relative">
                                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                                <Input
                                  value={styleSearch}
                                  onChange={(e) => setStyleSearch(e.target.value)}
                                  placeholder="Search styles"
                                  className="h-10 pl-9"
                                />
                              </div>
                              <div className="grid gap-3 md:grid-cols-2">
                                {filteredStyles.map((emotion) => {
                                  const isActive = selectedEmotion === emotion.value;
                                  return (
                                    <button
                                      key={emotion.value}
                                      type="button"
                                      onClick={() => updateVoiceSettings({ emotion: emotion.value, voice_id: formData.voice_settings?.voice_id || '' })}
                                      className={cn(
                                        'rounded-lg border p-4 text-left',
                                        isActive
                                          ? 'border-primary bg-primary/5'
                                          : 'card-border bg-gray-50/30 dark:bg-zinc-900/30'
                                      )}
                                    >
                                      <div className="flex items-center justify-between gap-3">
                                        <div>
                                          <p className="text-sm font-semibold text-heading">{emotion.label}</p>
                                          <p className="mt-1 text-xs text-subtle">{emotion.description}</p>
                                        </div>
                                        {isActive && (
                                          <Badge className="text-[10px] uppercase tracking-wide">Selected</Badge>
                                        )}
                                      </div>
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                            )}

                            {voiceEditorPanel === 'speed' && (
                            <div className="space-y-3 rounded-lg border border-dashed border-border bg-gray-50/30 dark:bg-zinc-900/30 p-4">
                              <div className="flex items-center justify-between gap-4">
                                <div>
                                  <Label className="text-sm font-semibold">Speed</Label>
                                  <p className="text-xs text-subtle">Tune cadence from measured to energetic.</p>
                                </div>
                                <div className="text-right">
                                  <p className="text-sm font-semibold text-heading">{selectedSpeed.toFixed(2)}</p>
                                  <p className="text-xs text-subtle">{describeVoiceSpeed(selectedSpeed)}</p>
                                </div>
                              </div>
                              <Slider
                                value={[selectedSpeed]}
                                min={0.6}
                                max={1.5}
                                step={0.05}
                                onValueChange={(value) => updateVoiceSettings({
                                  voice_id: formData.voice_settings?.voice_id || '',
                                  speed: Number(value[0].toFixed(2)),
                                })}
                              />
                              <div className="flex justify-between text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                                <span>Measured</span>
                                <span>Natural</span>
                                <span>Energetic</span>
                              </div>
                            </div>
                            )}

                            <div className="overflow-hidden rounded-lg border card-border bg-background/80">
                              <div className="border-b border-border/70 bg-gray-50/40 px-4 py-4 dark:bg-zinc-900/30 md:px-5">
                                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                                  <div className="space-y-1">
                                    <div className="flex flex-wrap items-center gap-2">
                                      <Label className="text-sm font-semibold">Pronunciation Library</Label>
                                      <Badge variant={pronunciationLibraryDirty ? 'default' : 'secondary'} className="text-[10px] uppercase tracking-wide">
                                        {pronunciationLibraryDirty ? 'Unsaved' : 'Saved'}
                                      </Badge>
                                    </div>
                                    <p className="text-xs text-subtle">
                                      Organization-level overrides for words and phrases. These apply to every agent in this org.
                                    </p>
                                  </div>
                                  <div className="flex flex-wrap items-center gap-2">
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      className="gap-1.5"
                                      onClick={addPronunciationEntry}
                                    >
                                      <Plus className="h-4 w-4" />
                                      Add Entry
                                    </Button>
                                    <Button
                                      type="button"
                                      size="sm"
                                      className="gap-1.5"
                                      onClick={savePronunciationLibrary}
                                      disabled={
                                        savePronunciationLibraryMutation.isPending
                                        || isPronunciationLibraryLoading
                                        || !pronunciationLibraryDirty
                                      }
                                    >
                                      {savePronunciationLibraryMutation.isPending ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                      ) : (
                                        <Save className="h-4 w-4" />
                                      )}
                                      {pronunciationLibraryDirty ? 'Save Changes' : 'Saved'}
                                    </Button>
                                  </div>
                                </div>
                                <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
                                  <span className="rounded-md border border-border/70 bg-background px-2 py-1 text-subtle">
                                    IPA example: <span className="font-mono text-foreground">&lt;&lt;həˈbəʊz&gt;&gt;</span>
                                  </span>
                                  <span className="rounded-md border border-border/70 bg-background px-2 py-1 text-subtle">
                                    Sounds-like example: <span className="font-mono text-foreground">hohbz</span>
                                  </span>
                                </div>
                                {hasIncompletePronunciationRow && (
                                  <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">
                                    Complete both fields for each row before saving.
                                  </p>
                                )}
                              </div>

                              <div className="space-y-4 px-4 py-4 md:px-5">
                                <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                                  <div className="relative w-full md:max-w-md">
                                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                                    <Input
                                      value={pronunciationSearch}
                                      onChange={(e) => setPronunciationSearch(e.target.value)}
                                      placeholder="Search words or pronunciations"
                                      className="h-10 pl-9"
                                    />
                                  </div>
                                  <p className="text-xs text-subtle">
                                    {filteredPronunciationRows.length} of {pronunciationEntries.length} entries · Max {MAX_PRONUNCIATION_ENTRIES}
                                  </p>
                                </div>

                                {isPronunciationLibraryLoading ? (
                                  <div className="flex items-center justify-center rounded-lg border border-dashed border-border bg-background/40 py-10 text-muted-foreground">
                                    <Loader2 className="h-5 w-5 animate-spin" />
                                  </div>
                                ) : filteredPronunciationRows.length === 0 ? (
                                  <div className="rounded-lg border border-dashed border-border bg-background/40 p-6 text-center">
                                    <p className="text-sm font-medium text-heading">
                                      {pronunciationEntries.length === 0 ? 'No pronunciation entries yet' : 'No entries match this search'}
                                    </p>
                                    <p className="mt-1 text-xs text-subtle">
                                      {pronunciationEntries.length === 0
                                        ? 'Add your first entry to enforce consistent pronunciation across all agents.'
                                        : 'Try a different search term.'}
                                    </p>
                                    {pronunciationEntries.length === 0 && (
                                      <Button type="button" variant="outline" size="sm" className="mt-4 gap-1.5" onClick={addPronunciationEntry}>
                                        <Plus className="h-4 w-4" />
                                        Add First Entry
                                      </Button>
                                    )}
                                  </div>
                                ) : (
                                  <div className="overflow-hidden rounded-lg border border-border/70">
                                    <div className="hidden grid-cols-[40px_minmax(0,1fr)_minmax(0,1fr)_48px] border-b border-border/70 bg-gray-50/60 px-3 py-2 text-xs font-medium text-subtle dark:bg-zinc-900/50 md:grid">
                                      <span>#</span>
                                      <span>Word or phrase</span>
                                      <span>Pronunciation (IPA or sounds-like)</span>
                                      <span />
                                    </div>
                                    <div className="divide-y divide-border/60">
                                      {filteredPronunciationRows.map(({ entry, index }) => (
                                        <div
                                          key={`pronunciation-row-${index}`}
                                          className="grid grid-cols-1 gap-3 px-3 py-3 md:grid-cols-[40px_minmax(0,1fr)_minmax(0,1fr)_48px] md:items-center"
                                        >
                                          <div className="hidden h-8 w-8 items-center justify-center rounded-md border border-border/70 text-xs text-subtle md:flex">
                                            {index + 1}
                                          </div>
                                          <div className="space-y-1">
                                            <Label className="text-[11px] text-subtle md:hidden">Word or phrase</Label>
                                            <Input
                                              value={entry.text}
                                              onChange={(e) => updatePronunciationEntry(index, 'text', e.target.value)}
                                              placeholder="Word or phrase"
                                              className="h-10"
                                            />
                                          </div>
                                          <div className="space-y-1">
                                            <Label className="text-[11px] text-subtle md:hidden">Pronunciation</Label>
                                            <Input
                                              value={entry.alias}
                                              onChange={(e) => updatePronunciationEntry(index, 'alias', e.target.value)}
                                              placeholder="Pronunciation (IPA or sounds-like)"
                                              className="h-10"
                                            />
                                          </div>
                                          <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => removePronunciationEntry(index)}
                                            aria-label="Remove entry"
                                            className="justify-self-end"
                                          >
                                            <X className="h-4 w-4" />
                                          </Button>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </TabsContent>

                      <TabsContent value="content" className="mt-0 space-y-6">
                        <div className="max-w-4xl divide-y divide-border/60">
                          <div className="grid gap-4 py-5 md:grid-cols-[220px_minmax(0,1fr)_auto] md:items-center">
                            <div>
                              <Label htmlFor="use_active_deck" className="text-sm font-medium cursor-pointer text-heading">Active Presentation Deck</Label>
                              <p className="mt-1 text-sm text-muted-foreground">Enable the agent to reference slide content.</p>
                            </div>
                            <p className="text-sm text-muted-foreground">Use the currently active deck during the demo narrative.</p>
                            <Switch
                              id="use_active_deck"
                              checked={formData.use_active_deck !== false}
                              onCheckedChange={(checked) => setFormData({ ...formData, use_active_deck: checked })}
                            />
                          </div>

                          <div className="grid gap-4 py-5 md:grid-cols-[220px_minmax(0,1fr)_auto] md:items-center">
                            <div>
                              <Label htmlFor="use_all_videos" className="text-sm font-medium cursor-pointer text-heading">All Organization Videos</Label>
                              <p className="mt-1 text-sm text-muted-foreground">Enable the agent to search video content.</p>
                            </div>
                            <p className="text-sm text-muted-foreground">Include organization video assets in the live retrieval set.</p>
                            <Switch
                              id="use_all_videos"
                              checked={formData.use_all_videos !== false}
                              onCheckedChange={(checked) => setFormData({ ...formData, use_all_videos: checked })}
                            />
                          </div>

                          <div className="grid gap-4 py-5 md:grid-cols-[220px_minmax(0,1fr)_auto] md:items-center">
                            <div>
                              <Label htmlFor="use_documentation" className="text-sm font-medium cursor-pointer text-heading">Documentation</Label>
                              <p className="mt-1 text-sm text-muted-foreground">Enable the agent to show documentation during sessions.</p>
                            </div>
                            <p className="text-sm text-muted-foreground">Include crawled documentation pages in the live retrieval set.</p>
                            <Switch
                              id="use_documentation"
                              checked={formData.use_documentation === true}
                              onCheckedChange={(checked) => setFormData({ ...formData, use_documentation: checked })}
                            />
                          </div>

                        </div>
                      </TabsContent>

                      <TabsContent value="conversation" className="mt-0 space-y-6">
                        <div className="max-w-4xl divide-y divide-border/60">
                          <div className="grid gap-4 py-5 md:grid-cols-[220px_minmax(0,1fr)]">
                            <div>
                              <Label htmlFor="conversation_control_mode" className="text-sm font-medium text-heading">Conversation Flow</Label>
                              <p className="mt-1 text-sm text-muted-foreground">Choose how assertively the agent should guide the visitor through the demo.</p>
                            </div>
                            <div className="space-y-3">
                              <Select
                                value={formData.conversation_control_mode || 'balanced'}
                                onValueChange={(value) => setFormData({ ...formData, conversation_control_mode: value as SalesAgent['conversation_control_mode'] })}
                              >
                                <SelectTrigger id="conversation_control_mode" className="h-11 rounded-2xl">
                                  <SelectValue placeholder="Select conversation style" />
                                </SelectTrigger>
                                <SelectContent>
                                  {CONVERSATION_CONTROL_MODE_OPTIONS.map((option) => (
                                    <SelectItem key={option.value} value={option.value}>
                                      {option.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <div className="rounded-2xl border border-border bg-card px-4 py-3">
                                <p className="text-sm font-medium text-heading">
                                  {getConversationControlModeOption(formData.conversation_control_mode).label}
                                </p>
                                <p className="mt-1 text-sm text-muted-foreground">
                                  {getConversationControlModeOption(formData.conversation_control_mode).description}
                                </p>
                              </div>
                            </div>
                          </div>

                          <div className="grid gap-4 py-5 md:grid-cols-[220px_minmax(0,1fr)_auto] md:items-center">
                            <div>
                              <Label htmlFor="auto_suggestions_enabled" className="text-sm font-medium cursor-pointer text-heading">Auto-Suggestions</Label>
                              <p className="mt-1 text-sm text-muted-foreground">Show follow-up suggestion pills to visitors during live sessions.</p>
                            </div>
                            <p className="text-sm text-muted-foreground">Changes take effect for new sessions after saving.</p>
                            <Switch
                              id="auto_suggestions_enabled"
                              checked={formData.auto_suggestions_enabled !== false}
                              onCheckedChange={(checked) => setFormData({ ...formData, auto_suggestions_enabled: checked })}
                            />
                          </div>

                          {formData.auto_suggestions_enabled !== false && (
                          <div className="grid gap-4 py-5 md:grid-cols-[220px_minmax(0,1fr)]">
                            <div>
                              <Label htmlFor="suggestion_instructions" className="text-sm font-medium text-heading">Suggestion Guidance</Label>
                              <p className="mt-1 text-sm text-muted-foreground">Shape how follow-up suggestion chips guide the conversation after each turn.</p>
                            </div>
                            <div className="space-y-2">
                              <Textarea
                                id="suggestion_instructions"
                                value={formData.suggestion_instructions || ''}
                                onChange={(e) => setFormData({ ...formData, suggestion_instructions: e.target.value })}
                                placeholder="Example: Keep one suggestion focused on ROI, one on implementation, and avoid pricing unless the visitor asks."
                                className="min-h-[140px] rounded-2xl"
                                maxLength={2000}
                              />
                              <p className="text-xs text-subtle">
                                This guidance applies only to suggestion chips, not the core spoken prompt.
                              </p>
                            </div>
                          </div>
                          )}
                        </div>
                      </TabsContent>

                      <TabsContent value="calendar" className="mt-0 space-y-6">
                        <div className="max-w-4xl divide-y divide-border/60">
                          <div className="grid gap-4 py-5 md:grid-cols-[220px_minmax(0,1fr)]">
                            <div>
                              <Label htmlFor="calendar_link" className="text-sm font-medium text-heading">Calendar Link</Label>
                              <p className="mt-1 text-sm text-muted-foreground">Booking destination used for follow-up meetings.</p>
                            </div>
                            <div className="space-y-2">
                              <Input
                                id="calendar_link"
                                type="url"
                                value={formData.calendar_link || ''}
                                onChange={(e) => setFormData({ ...formData, calendar_link: e.target.value })}
                                placeholder="https://cal.com/your-company/demo"
                                className="h-11 rounded-2xl"
                              />
                              <p className="text-xs text-subtle">Leave blank to use the Hobbes default booking destination.</p>
                            </div>
                          </div>
                        </div>
                      </TabsContent>

                      <TabsContent value="entryModal" className="mt-0 space-y-6">
                        <div className="max-w-none">
                          <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_320px]">
                            <div className="space-y-6">
                              <div className="rounded-xl border border-border bg-card p-5">
                                <div className="flex items-center justify-between gap-4">
                                  <div>
                                    <Label className="text-sm font-medium text-heading">Cover wordmark</Label>
                                    <p className="mt-1 text-sm text-muted-foreground">Wordmark overlaid on top of the entry modal cover image.</p>
                                  </div>
                                  <div className="shrink-0">
                                    {formData.entry_modal_logo_url ? (
                                      <div className="relative rounded-2xl border card-border" style={{ backgroundImage: 'repeating-conic-gradient(#d1d5db 0% 25%, transparent 0% 50%)', backgroundSize: '16px 16px' }}>
                                        <img
                                          src={formData.entry_modal_logo_url}
                                          alt="Entry modal cover wordmark"
                                          className="h-12 w-auto object-contain rounded-2xl"
                                        />
                                        <Button
                                          type="button"
                                          variant="ghost"
                                          size="icon"
                                          className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-destructive text-destructive-foreground"
                                          onClick={() => setFormData({ ...formData, entry_modal_logo_url: '' })}
                                        >
                                          <X className="h-3 w-3" />
                                        </Button>
                                      </div>
                                    ) : (
                                      <div className="flex h-12 w-24 items-center justify-center rounded-2xl border-2 border-dashed border-muted-foreground/25 bg-gray-50/50 dark:bg-zinc-900/50">
                                        <Image className="h-6 w-6 text-muted-foreground/50" />
                                      </div>
                                    )}
                                  </div>
                                </div>

                                <div className="mt-4 flex flex-wrap gap-2">
                                  <Input
                                    type="url"
                                    placeholder="https://your-logo-url.com/logo.png"
                                    value={formData.entry_modal_logo_url || ''}
                                    onChange={(e) => setFormData({ ...formData, entry_modal_logo_url: e.target.value })}
                                    className="h-11 min-w-[240px] flex-1 rounded-2xl"
                                  />
                                  <input
                                    ref={logoInputRef}
                                    type="file"
                                    accept=".png,.jpg,.jpeg,.webp,.gif,.svg"
                                    className="hidden"
                                    onChange={(e) => {
                                      const file = e.target.files?.[0];
                                      if (file) handleImageUpload(file, 'entry_modal_logo_url', 'agent_logo', setIsUploadingLogo);
                                      e.target.value = '';
                                    }}
                                  />
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    disabled={isUploadingLogo}
                                    onClick={() => logoInputRef.current?.click()}
                                    className="h-11 gap-1.5 rounded-2xl"
                                  >
                                    {isUploadingLogo ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                                    {isUploadingLogo ? 'Uploading...' : 'Upload'}
                                  </Button>
                                </div>
                              </div>

                              <div className="rounded-xl border border-border bg-card p-5">
                                <div className="flex items-center justify-between gap-4">
                                  <div>
                                    <Label className="text-sm font-medium text-heading">Header wordmark</Label>
                                    <p className="mt-1 text-sm text-muted-foreground">Light wordmark shown in the top-left of the widget launcher header. Falls back to the cover wordmark when empty.</p>
                                  </div>
                                  <div className="shrink-0">
                                    {formData.header_logo_url ? (
                                      <div className="relative rounded-2xl border card-border bg-zinc-900">
                                        <img
                                          src={formData.header_logo_url}
                                          alt="Widget header wordmark"
                                          className="h-12 w-auto object-contain rounded-2xl"
                                        />
                                        <Button
                                          type="button"
                                          variant="ghost"
                                          size="icon"
                                          className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-destructive text-destructive-foreground"
                                          onClick={() => setFormData({ ...formData, header_logo_url: '' })}
                                        >
                                          <X className="h-3 w-3" />
                                        </Button>
                                      </div>
                                    ) : (
                                      <div className="flex h-12 w-24 items-center justify-center rounded-2xl border-2 border-dashed border-muted-foreground/25 bg-zinc-900/50">
                                        <Image className="h-6 w-6 text-muted-foreground/50" />
                                      </div>
                                    )}
                                  </div>
                                </div>

                                <div className="mt-4 flex flex-wrap gap-2">
                                  <Input
                                    type="url"
                                    placeholder="https://your-logo-url.com/light-wordmark.png"
                                    value={formData.header_logo_url || ''}
                                    onChange={(e) => setFormData({ ...formData, header_logo_url: e.target.value })}
                                    className="h-11 min-w-[240px] flex-1 rounded-2xl"
                                  />
                                  <input
                                    ref={headerLogoInputRef}
                                    type="file"
                                    accept=".png,.jpg,.jpeg,.webp,.gif,.svg"
                                    className="hidden"
                                    onChange={(e) => {
                                      const file = e.target.files?.[0];
                                      if (file) handleImageUpload(file, 'header_logo_url', 'agent_logo', setIsUploadingHeaderLogo);
                                      e.target.value = '';
                                    }}
                                  />
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    disabled={isUploadingHeaderLogo}
                                    onClick={() => headerLogoInputRef.current?.click()}
                                    className="h-11 gap-1.5 rounded-2xl"
                                  >
                                    {isUploadingHeaderLogo ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                                    {isUploadingHeaderLogo ? 'Uploading...' : 'Upload'}
                                  </Button>
                                </div>
                                <p className="mt-2 text-xs text-subtle">Upload a light/transparent variant — it appears on the dark glass header.</p>
                              </div>

                              <div className="rounded-xl border border-border bg-background">
                                <div className="grid gap-4 px-5 py-5 md:grid-cols-2">
                                  <div className="space-y-2">
                                    <Label htmlFor="entry_modal_title" className="text-sm font-medium text-heading">Title</Label>
                                    <Input
                                      id="entry_modal_title"
                                      value={formData.entry_modal_title || ''}
                                      onChange={(e) => setFormData({ ...formData, entry_modal_title: e.target.value })}
                                      placeholder="Experience Acme Corp"
                                      className="h-11 rounded-2xl"
                                    />
                                  </div>

                                  <div className="space-y-2">
                                    <Label htmlFor="entry_modal_button_text" className="text-sm font-medium text-heading">Button Text</Label>
                                    <Input
                                      id="entry_modal_button_text"
                                      value={formData.entry_modal_button_text || ''}
                                      onChange={(e) => setFormData({ ...formData, entry_modal_button_text: e.target.value })}
                                      placeholder="Start Your Personalized Demo"
                                      className="h-11 rounded-2xl"
                                    />
                                  </div>
                                </div>

                                <div className="border-t border-border px-5 py-5">
                                  <div className="space-y-2">
                                    <Label htmlFor="entry_modal_description" className="text-sm font-medium text-heading">Description</Label>
                                    <Textarea
                                      id="entry_modal_description"
                                      value={formData.entry_modal_description || ''}
                                      onChange={(e) => setFormData({ ...formData, entry_modal_description: e.target.value })}
                                      placeholder="Discover how our AI can transform your revenue engine and supercharge your sales process."
                                      rows={3}
                                      className="resize-none rounded-2xl"
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="rounded-xl border border-border bg-card p-5">
                              <div>
                                <Label className="text-sm font-medium text-heading">Contact Fields</Label>
                                <p className="mt-1 text-sm text-muted-foreground">Choose what visitors are asked for before the demo starts.</p>
                              </div>

                              <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-1">
                                {[
                                  { key: 'lead_capture_name', label: 'Name' },
                                  { key: 'lead_capture_email', label: 'Email' },
                                  { key: 'lead_capture_phone', label: 'Phone' },
                                  { key: 'lead_capture_business', label: 'Company' },
                                ].map((field) => (
                                  <div key={field.key} className="rounded-xl border border-border bg-background px-4 py-4">
                                    <p className="text-sm font-medium text-heading">{field.label}</p>
                                    <Select
                                      value={(formData[field.key as keyof SalesAgent] as LeadCaptureState | undefined) || 'hidden'}
                                      onValueChange={(value: LeadCaptureState) => setFormData({ ...formData, [field.key]: value })}
                                    >
                                      <SelectTrigger className="mt-3 w-full rounded-2xl">
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {LEAD_CAPTURE_FIELD_OPTIONS.map((option) => (
                                          <SelectItem key={option.value} value={option.value}>
                                            {option.label}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      </TabsContent>

                      <TabsContent value="launcherTrigger" className="mt-0 space-y-6">
                        <div className="max-w-none space-y-6">
                          <div className="grid gap-4 lg:grid-cols-2">
                              {/* Icon upload */}
                              <div className="rounded-xl border border-border bg-card p-5">
                                <div className="flex items-center justify-between gap-4">
                                  <div>
                                    <Label className="text-sm font-medium text-heading">Trigger Icon</Label>
                                    <p className="mt-1 text-sm text-muted-foreground">Square image displayed as a circular avatar in the trigger pill.</p>
                                  </div>
                                  <div className="shrink-0">
                                    {formData.sticky_trigger_icon_url ? (
                                      <div className="relative rounded-full border card-border" style={{ backgroundImage: 'repeating-conic-gradient(#d1d5db 0% 25%, transparent 0% 50%)', backgroundSize: '16px 16px' }}>
                                        <img
                                          src={formData.sticky_trigger_icon_url}
                                          alt="Launcher trigger icon"
                                          className="h-10 w-10 object-cover rounded-full"
                                        />
                                        <Button
                                          type="button"
                                          variant="ghost"
                                          size="icon"
                                          className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-destructive text-destructive-foreground"
                                          onClick={() => setFormData({ ...formData, sticky_trigger_icon_url: '' })}
                                        >
                                          <X className="h-3 w-3" />
                                        </Button>
                                      </div>
                                    ) : (
                                      <div className="flex h-10 w-10 items-center justify-center rounded-full border-2 border-dashed border-muted-foreground/25 bg-gray-50/50 dark:bg-zinc-900/50">
                                        <Image className="h-5 w-5 text-muted-foreground/50" />
                                      </div>
                                    )}
                                  </div>
                                </div>

                                <div className="mt-4 flex flex-wrap gap-2">
                                  <Input
                                    type="url"
                                    placeholder="https://your-icon-url.com/icon.png"
                                    value={formData.sticky_trigger_icon_url || ''}
                                    onChange={(e) => setFormData({ ...formData, sticky_trigger_icon_url: e.target.value })}
                                    className="h-11 min-w-[240px] flex-1 rounded-2xl"
                                  />
                                  <input
                                    ref={launcherIconInputRef}
                                    type="file"
                                    accept=".png,.jpg,.jpeg,.webp,.gif,.svg"
                                    className="hidden"
                                    onChange={(e) => {
                                      const file = e.target.files?.[0];
                                      if (file) handleImageUpload(file, 'sticky_trigger_icon_url', 'agent_icon', setIsUploadingLauncherIcon);
                                      e.target.value = '';
                                    }}
                                  />
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    disabled={isUploadingLauncherIcon}
                                    onClick={() => launcherIconInputRef.current?.click()}
                                    className="h-11 gap-1.5 rounded-2xl"
                                  >
                                    {isUploadingLauncherIcon ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                                    {isUploadingLauncherIcon ? 'Uploading...' : 'Upload'}
                                  </Button>
                                </div>
                                <p className="mt-2 text-xs text-subtle">PNG, JPG, WebP, GIF, or SVG. Square images work best.</p>
                              </div>

                              {/* Text */}
                              <div className="rounded-xl border border-border bg-background">
                                <div className="grid gap-4 px-5 py-5 md:grid-cols-3">
                                    <div className="space-y-2">
                                      <Label htmlFor="sticky_trigger_text" className="text-sm font-medium text-heading">Button Text</Label>
                                      <Input
                                        id="sticky_trigger_text"
                                        value={formData.sticky_trigger_text || ''}
                                        onChange={(e) => setFormData({ ...formData, sticky_trigger_text: e.target.value })}
                                        placeholder="Start Demo"
                                        className="h-11 rounded-2xl"
                                      />
                                    </div>

                                    <div className="space-y-2">
                                      <Label className="text-sm font-medium text-heading">Font Family</Label>
                                      <Select
                                        value={formData.sticky_trigger_font_family || '__default__'}
                                        onValueChange={(val) => setFormData({ ...formData, sticky_trigger_font_family: val === '__default__' ? null : val })}
                                      >
                                        <SelectTrigger className="h-11 rounded-2xl">
                                          <SelectValue placeholder="Default (system font)" />
                                        </SelectTrigger>
                                        <SelectContent className="max-h-60">
                                          <SelectItem value="__default__">Default (system font)</SelectItem>
                                          {googleFonts.map((font) => (
                                            <SelectItem key={font.value} value={font.value}>
                                              {font.name}
                                            </SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    </div>

                                    <div className="space-y-2">
                                      <Label className="text-sm font-medium text-heading">Tint</Label>
                                      <LauncherTintPicker
                                        value={formData.sticky_trigger_bg_color}
                                        onChange={(value) => setFormData({ ...formData, sticky_trigger_bg_color: value })}
                                      />
                                    </div>
                                  </div>

                                <div className="grid gap-4 border-t border-border px-5 py-5 md:grid-cols-2">
                                  <div className="space-y-2">
                                    <Label className="text-sm font-medium text-heading">Icon background color</Label>
                                    <p className="text-xs text-muted-foreground">Color of the small circle behind the launcher icon. Defaults to a dark grey when empty.</p>
                                    <LauncherTintPicker
                                      value={formData.launcher_icon_bg_color}
                                      onChange={(value) => setFormData({ ...formData, launcher_icon_bg_color: value })}
                                    />
                                  </div>
                                </div>
                              </div>

                              {/* Hover thumbnail upload */}
                              <div className="rounded-xl border border-border bg-card p-5">
                                <div>
                                  <Label className="text-sm font-medium text-heading">Hover Thumbnail</Label>
                                  <p className="mt-1 text-sm text-muted-foreground">Optional 16:9 image shown when visitors hover the trigger pill. Leave empty to keep the simple pill.</p>
                                </div>

                                <div className="mt-4 flex min-h-[160px] items-center justify-center rounded-xl border border-border bg-background p-4">
                                  {formData.sticky_trigger_thumbnail_url ? (
                                    <div
                                      className="relative w-full max-w-sm overflow-hidden rounded-lg"
                                      style={{ backgroundImage: 'repeating-conic-gradient(#d1d5db 0% 25%, transparent 0% 50%)', backgroundSize: '16px 16px' }}
                                    >
                                      <div className="aspect-video w-full">
                                        <img
                                          src={formData.sticky_trigger_thumbnail_url}
                                          alt="Launcher hover thumbnail"
                                          className="h-full w-full object-cover"
                                        />
                                      </div>
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-destructive text-destructive-foreground"
                                        onClick={() => setFormData({ ...formData, sticky_trigger_thumbnail_url: '' })}
                                      >
                                        <X className="h-3 w-3" />
                                      </Button>
                                    </div>
                                  ) : (
                                    <div className="text-center">
                                      <Image className="mx-auto h-10 w-10 text-muted-foreground/50" />
                                      <p className="mt-3 text-sm font-medium text-heading">No hover thumbnail</p>
                                      <p className="mt-1 text-xs text-muted-foreground">Upload a 16:9 image to show a preview on hover.</p>
                                    </div>
                                  )}
                                </div>

                                <div className="mt-4 flex flex-wrap gap-2">
                                  <Input
                                    type="url"
                                    placeholder="https://your-thumbnail-url.com/image.jpg"
                                    value={formData.sticky_trigger_thumbnail_url || ''}
                                    onChange={(e) => setFormData({ ...formData, sticky_trigger_thumbnail_url: e.target.value })}
                                    className="h-11 min-w-[240px] flex-1 rounded-2xl"
                                  />
                                  <input
                                    ref={launcherThumbnailInputRef}
                                    type="file"
                                    accept=".png,.jpg,.jpeg,.webp,.gif"
                                    className="hidden"
                                    onChange={(e) => {
                                      const file = e.target.files?.[0];
                                      if (file) handleImageUpload(file, 'sticky_trigger_thumbnail_url', 'launcher_thumbnail', setIsUploadingLauncherThumbnail);
                                      e.target.value = '';
                                    }}
                                  />
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    disabled={isUploadingLauncherThumbnail}
                                    onClick={() => launcherThumbnailInputRef.current?.click()}
                                    className="h-11 gap-1.5 rounded-2xl"
                                  >
                                    {isUploadingLauncherThumbnail ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                                    {isUploadingLauncherThumbnail ? 'Uploading...' : 'Upload'}
                                  </Button>
                                </div>
                                <p className="mt-2 text-xs text-subtle">PNG, JPG, WebP, or GIF. 16:9 images work best — other aspects will be cropped.</p>
                              </div>
                          </div>

                          <div className="rounded-xl border border-border bg-card p-5">
                            <div>
                              <Label className="text-sm font-medium text-heading">Preview</Label>
                              <p className="mt-1 text-sm text-muted-foreground">
                                {formData.sticky_trigger_thumbnail_url
                                  ? 'Switch between icon, rest, and open states. The thumbnail appears with the open launcher.'
                                  : 'Switch between icon, rest, and open states from the latest launcher.'}
                              </p>
                            </div>
                            {formData.sticky_trigger_font_family && (
                              <link
                                rel="stylesheet"
                                href={`https://fonts.googleapis.com/css2?family=${encodeURIComponent(formData.sticky_trigger_font_family)}&display=swap`}
                              />
                            )}
                            <div className="mt-5 relative h-[460px] w-full overflow-hidden rounded-xl border border-border bg-background">
                              <StickyTriggerMorphPreview
                                placement={DEFAULT_STICKY_TRIGGER_PLACEMENT}
                                iconUrl={formData.sticky_trigger_icon_url || ''}
                                text={formData.sticky_trigger_text || ''}
                                tintColor={formData.sticky_trigger_bg_color || null}
                                textColor={formData.sticky_trigger_text_color || null}
                                fontFamily={formData.sticky_trigger_font_family || null}
                                thumbnailUrl={formData.sticky_trigger_thumbnail_url || ''}
                                leadCaptureEmail={formData.lead_capture_email || 'required'}
                                iconBgColor={formData.launcher_icon_bg_color || null}
                              />
                            </div>
                          </div>
                        </div>
                      </TabsContent>

                      <TabsContent value="launchState" className="mt-0 space-y-6">
                        <div className="max-w-5xl space-y-8">
                          <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
                            <div className="space-y-3 rounded-xl border border-border bg-card p-5">
                              <div>
                                <Label className="text-sm font-medium text-heading">Starting Image</Label>
                                <p className="mt-1 text-sm text-muted-foreground">Displayed in the viewer before any demo content is presented.</p>
                              </div>

                              <div className="flex min-h-[260px] items-center justify-center rounded-xl border border-border bg-background p-6">
                                {formData.background_screenshot_url ? (
                                  <div
                                    className="relative flex w-full items-center justify-center rounded-xl"
                                    style={{ backgroundImage: 'repeating-conic-gradient(#d1d5db 0% 25%, transparent 0% 50%)', backgroundSize: '16px 16px' }}
                                  >
                                    <img
                                      src={formData.background_screenshot_url}
                                      alt="Background screenshot"
                                      className="max-h-[220px] w-auto max-w-full object-contain rounded-xl"
                                    />
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-destructive text-destructive-foreground"
                                      onClick={() => setFormData({ ...formData, background_screenshot_url: '' })}
                                    >
                                      <X className="h-3 w-3" />
                                    </Button>
                                  </div>
                                ) : (
                                  <div className="text-center">
                                    <Image className="mx-auto h-10 w-10 text-muted-foreground/50" />
                                    <p className="mt-4 text-sm font-medium text-heading">No starting image selected</p>
                                    <p className="mt-1 text-sm text-muted-foreground">Add an image to create a more polished launch state.</p>
                                  </div>
                                )}
                              </div>
                            </div>

                            <div className="space-y-3 rounded-xl border border-border bg-card p-5">
                              <div>
                                <Label className="text-sm font-medium text-heading">Asset Source</Label>
                                <p className="mt-1 text-sm text-muted-foreground">Provide an image URL or upload a file directly.</p>
                              </div>
                              <div className="space-y-2">
                                <Input
                                  type="url"
                                  placeholder="https://example.com/screenshot.png"
                                  value={formData.background_screenshot_url || ''}
                                  onChange={(e) => setFormData({ ...formData, background_screenshot_url: e.target.value })}
                                  className="h-11 rounded-2xl"
                                />
                                <input
                                  ref={backgroundInputRef}
                                  type="file"
                                  accept=".png,.jpg,.jpeg,.webp,.gif,.svg"
                                  className="hidden"
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) handleImageUpload(file, 'background_screenshot_url', 'background_screenshot', setIsUploadingBackground);
                                    e.target.value = '';
                                  }}
                                />
                                <Button
                                  type="button"
                                  variant="outline"
                                  disabled={isUploadingBackground}
                                  onClick={() => backgroundInputRef.current?.click()}
                                  className="h-11 w-full gap-1.5 rounded-2xl"
                                >
                                  {isUploadingBackground ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                                  {isUploadingBackground ? 'Uploading...' : 'Upload Image'}
                                </Button>
                                <p className="text-xs text-subtle">PNG, JPG, WebP, GIF, or SVG. Landscape imagery works best here.</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </TabsContent>

                      <TabsContent value="terminology" className="mt-0 space-y-6">
                        <div className="max-w-4xl space-y-6">
                          <div className="flex items-center gap-3">
                            <Select
                              onValueChange={(country) => {
                                const bank = { ...(formData.terminology_bank || {}) };
                                if (!bank[country]) {
                                  bank[country] = [];
                                }
                                setFormData({ ...formData, terminology_bank: bank });
                              }}
                            >
                              <SelectTrigger className="h-11 w-64 rounded-2xl">
                                <SelectValue placeholder="Add a region..." />
                              </SelectTrigger>
                              <SelectContent>
                                {(terminologyCountries ?? []).filter((c) => !formData.terminology_bank?.[c]).map((country) => (
                                  <SelectItem key={country} value={country}>{country}</SelectItem>
                                ))}
                                {['AU', 'GB', 'US', 'CA', 'NZ', 'IE', 'IN', 'SG', 'ZA', 'DE', 'FR', 'JP']
                                  .filter((c) => !formData.terminology_bank?.[c] && !(terminologyCountries ?? []).includes(c))
                                  .map((country) => (
                                    <SelectItem key={country} value={country}>{country}</SelectItem>
                                  ))}
                              </SelectContent>
                            </Select>
                            <p className="text-sm text-muted-foreground">Select a country code to add region-specific terms.</p>
                          </div>

                          {formData.terminology_bank && Object.keys(formData.terminology_bank).length > 0 ? (
                            Object.entries(formData.terminology_bank).map(([country, entries]) => (
                              <div key={country} className="rounded-xl border border-border bg-card">
                                <div className="flex items-center justify-between border-b border-border px-5 py-3">
                                  <h3 className="text-sm font-semibold text-heading flex items-center gap-2">
                                    <Globe2 className="h-4 w-4 text-muted-foreground" />
                                    {country}
                                  </h3>
                                  <div className="flex items-center gap-2">
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      className="h-8 gap-1 text-xs"
                                      onClick={() => {
                                        const bank = { ...(formData.terminology_bank || {}) };
                                        bank[country] = [...(bank[country] || []), { term: '', context: '' }];
                                        setFormData({ ...formData, terminology_bank: bank });
                                      }}
                                    >
                                      <Plus className="h-3 w-3" /> Add Term
                                    </Button>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      className="h-8 text-xs text-destructive hover:text-destructive"
                                      onClick={() => {
                                        const bank = { ...(formData.terminology_bank || {}) };
                                        delete bank[country];
                                        setFormData({ ...formData, terminology_bank: Object.keys(bank).length > 0 ? bank : null });
                                      }}
                                    >
                                      <Trash2 className="h-3 w-3" />
                                    </Button>
                                  </div>
                                </div>

                                <div className="divide-y divide-border/60">
                                  {(entries as TerminologyEntry[]).map((entry, idx) => (
                                    <div key={idx} className="flex items-start gap-3 px-5 py-3">
                                      <div className="flex-1 space-y-2">
                                        <Input
                                          placeholder="Term (e.g. organisation)"
                                          value={entry.term}
                                          onChange={(e) => {
                                            const bank = { ...(formData.terminology_bank || {}) };
                                            const list = [...(bank[country] || [])];
                                            list[idx] = { ...list[idx], term: e.target.value };
                                            bank[country] = list;
                                            setFormData({ ...formData, terminology_bank: bank });
                                          }}
                                          className="h-9 rounded-xl"
                                        />
                                        <Input
                                          placeholder="Context (optional — e.g. Use Australian English spelling)"
                                          value={entry.context || ''}
                                          onChange={(e) => {
                                            const bank = { ...(formData.terminology_bank || {}) };
                                            const list = [...(bank[country] || [])];
                                            list[idx] = { ...list[idx], context: e.target.value || undefined };
                                            bank[country] = list;
                                            setFormData({ ...formData, terminology_bank: bank });
                                          }}
                                          className="h-9 rounded-xl"
                                        />
                                      </div>
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        className="mt-1 h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
                                        onClick={() => {
                                          const bank = { ...(formData.terminology_bank || {}) };
                                          const list = [...(bank[country] || [])];
                                          list.splice(idx, 1);
                                          if (list.length === 0) {
                                            delete bank[country];
                                            setFormData({ ...formData, terminology_bank: Object.keys(bank).length > 0 ? bank : null });
                                          } else {
                                            bank[country] = list;
                                            setFormData({ ...formData, terminology_bank: bank });
                                          }
                                        }}
                                      >
                                        <X className="h-3.5 w-3.5" />
                                      </Button>
                                    </div>
                                  ))}
                                  {(entries as TerminologyEntry[]).length === 0 && (
                                    <div className="px-5 py-6 text-center text-sm text-muted-foreground">
                                      No terms yet. Click "Add Term" to get started.
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border py-16">
                              <Languages className="h-10 w-10 text-muted-foreground/40" />
                              <p className="mt-4 text-sm font-medium text-heading">No terminology configured</p>
                              <p className="mt-1 text-sm text-muted-foreground">Select a region above to start adding terms.</p>
                            </div>
                          )}
                        </div>
                      </TabsContent>
                    </div>
                  </form>
                )}
                </main>
              )}
            </div>
          </Tabs>
        </div>
      </div>
    </>
  );
};

export default SalesAgentContent;
